<template>
  <div>
    <spinner-loader :loadingStuff="loadingStuff" />
    <h3>Game match reports</h3>

    <a href="javascript:;" class="view-all-reports" @click="openAllGamegReportsModal('allGameReportsModal')">
      <img class="large-icon-shape" src="./img/game-ass.svg" alt="" />
      <span>View all <br />
        Game match reports</span>
    </a>

    <b-modal v-if="showAllGameReportsModal" v-model="showAllGameReportsModal" size="lg" modal-class="modal-fullscreen"
      title="Game match reports" :hide-footer="true">
      <!-- Season Selector -->
      <div class="season-selector">
        <b-button-group>
          <b-button :variant="selectedSeason === 'current' ? 'primary' : 'outline-primary'
            " @click="switchSeason('current')">
            Current Season
          </b-button>
          <b-button :variant="selectedSeason === 'last' ? 'primary' : 'outline-primary'" @click="switchSeason('last')">
            Last Season
          </b-button>
          <b-button :variant="selectedSeason === 'summary' ? 'primary' : 'outline-primary'"
            @click="switchSeason('summary')">
            <i class="bi bi-bar-chart-fill"></i> Season stats
          </b-button>
        </b-button-group>
      </div>

      <!-- Summary Dashboard View -->
      <div v-if="selectedSeason === 'summary'" class="summary-dashboard">
        <h3>Performance Summary Dashboard</h3>

        <!-- Timeline Picker -->
        <div class="timeline-picker">
          <label class="form__label">Select Timeline:</label>
          <b-form-select v-model="summaryTimeline" @change="updateSummaryData">
            <option value="last30">Last 30 Days</option>
            <option value="last90">Last 90 Days</option>
            <option value="currentSeason">Current Season</option>
            <option value="lastSeason">Last Season</option>
            <option value="allTime">All Time</option>
          </b-form-select>
        </div>

        <!-- Stats Overview Cards -->
        <div class="stats-overview">
          <div class="stat-card">
            <div class="stat-icon games"><i class="bi bi-calendar-check"></i></div>
            <div class="stat-content">
              <span class="stat-number">{{ summaryStats.totalGames }}</span>
              <span class="stat-label">Games Played</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon goals"><i class="bi bi-trophy"></i></div>
            <div class="stat-content">
              <span class="stat-number">{{ summaryStats.totalGoals }}</span>
              <span class="stat-label">Goals</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon assists"><i class="bi bi-hand-thumbs-up"></i></div>
            <div class="stat-content">
              <span class="stat-number">{{ summaryStats.totalAssists }}</span>
              <span class="stat-label">Assists</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon time"><i class="bi bi-clock"></i></div>
            <div class="stat-content">
              <span class="stat-number">{{ summaryStats.totalMinutes }}</span>
              <span class="stat-label">Minutes Played</span>
            </div>
          </div>
        </div>

        <!-- Secondary Stats -->
        <div class="stats-secondary">
          <div class="stat-card-small">
            <span class="stat-label">Avg Minutes/Game</span>
            <span class="stat-value">{{ summaryStats.avgMinutesPerGame }}</span>
          </div>
          <div class="stat-card-small">
            <span class="stat-label">Goals/Game</span>
            <span class="stat-value">{{ summaryStats.goalsPerGame }}</span>
          </div>
          <div class="stat-card-small">
            <span class="stat-label">Assists/Game</span>
            <span class="stat-value">{{ summaryStats.assistsPerGame }}</span>
          </div>
          <div class="stat-card-small yellow">
            <span class="stat-label">Yellow Cards</span>
            <span class="stat-value">{{ summaryStats.yellowCards }}</span>
          </div>
          <div class="stat-card-small red">
            <span class="stat-label">Red Cards</span>
            <span class="stat-value">{{ summaryStats.redCards }}</span>
          </div>
        </div>

        <!-- Coach Ratings Summary -->
        <div class="ratings-summary" v-if="summaryStats.totalCoachReports > 0">
          <h4>Coach Ratings Overview</h4>
          <div class="ratings-breakdown">
            <div class="rating-bar-container">
              <div class="rating-bar">
                <div class="rating-fill excellent" :style="{ width: summaryStats.ratingPercentages.excellent + '%' }">
                </div>
                <div class="rating-fill good" :style="{ width: summaryStats.ratingPercentages.good + '%' }"></div>
                <div class="rating-fill average" :style="{ width: summaryStats.ratingPercentages.average + '%' }"></div>
                <div class="rating-fill belowAverage"
                  :style="{ width: summaryStats.ratingPercentages.belowAverage + '%' }"></div>
                <div class="rating-fill poor" :style="{ width: summaryStats.ratingPercentages.poor + '%' }"></div>
              </div>
            </div>
            <div class="rating-legend">
              <span class="legend-item"><span class="dot excellent"></span> Excellent ({{ summaryStats.ratings.excellent
                }})</span>
              <span class="legend-item"><span class="dot good"></span> Good ({{ summaryStats.ratings.good }})</span>
              <span class="legend-item"><span class="dot average"></span> Progressing well ({{
                summaryStats.ratings.average
                }})</span>
              <span class="legend-item"><span class="dot belowAverage"></span> ⁠⁠Requires improvement ({{
                summaryStats.ratings.belowAverage }})</span>
              <span class="legend-item"><span class="dot poor"></span> Unsatisfactory ({{ summaryStats.ratings.poor
                }})</span>
            </div>
          </div>
          <p class="coach-reports-count">Based on {{ summaryStats.totalCoachReports }} coach reports</p>
        </div>

        <!-- Positions Played -->
        <div class="positions-summary" v-if="Object.keys(summaryStats.positionsPlayed).length > 0">
          <h4>Positions Played</h4>
          <div class="positions-list">
            <div class="position-item" v-for="(count, position) in summaryStats.positionsPlayed" :key="position">
              <span class="position-name">{{ position }}</span>
              <span class="position-count">{{ count }} games</span>
            </div>
          </div>
        </div>

        <!-- Report Completion Status -->
        <div class="completion-summary">
          <h4>Report Completion</h4>
          <div class="completion-stats">
            <div class="completion-item">
              <div class="completion-circle"
                :class="{ complete: summaryStats.playerReportsComplete === summaryStats.totalGames }">
                <span>{{ summaryStats.playerReportsComplete }}/{{ summaryStats.totalGames }}</span>
              </div>
              <span class="completion-label">Player Reports</span>
            </div>
            <div class="completion-item">
              <div class="completion-circle"
                :class="{ complete: summaryStats.totalCoachReports === summaryStats.totalGames }">
                <span>{{ summaryStats.totalCoachReports }}/{{ summaryStats.totalGames }}</span>
              </div>
              <span class="completion-label">Coach Reports</span>
            </div>
          </div>
        </div>
      </div>

      <h3 v-if="selectedSeason !== 'summary'">{{ selectedSeasonLabel }} reports</h3>

      <!-- Loading state -->
      <div v-if="loadingLastSeason" class="text-center">
        <div class="loading-spinner"></div>
        <p>Loading last season reports...</p>
      </div>

      <div v-else-if="selectedSeason !== 'summary'" class="all-game-reports">
        <div v-if="sortedSeasonReports.length === 0" class="empty-state list-empty-state">
          <img src="./img/emptyStateIllustration.svg" alt="" />
          <p>No game reports for this season yet.</p>
        </div>

        <div v-else class="reports-table-wrap">
          <table class="w-a-style game-assesment-reports-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Opposition</th>
                <th>Reports</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              <!-- Key on gameId: a fixture with no report yet has a null
                   report id, and several nulls would collide. -->
              <tr v-for="session in sortedSeasonReports" :key="session.gameId" :class="[
                getReportClass(session),
                { selected: selectedGameReport.gameId === session.gameId },
              ]" @click="viewFullReport(session.gameId)">
                <td>{{ formatDateForHuman(session.matchDate) }}</td>
                <td>
                  {{ session.opposition }}
                  <span v-if="reportLevel(session)" class="report-level" :class="reportLevel(session)" v-b-tooltip.hover
                    :title="reportLevel(session) === 'up'
                      ? 'Played up an age group in this game'
                      : 'Played down an age group in this game'">
                    {{ teamULabel(session.team_age_group) }}
                  </span>
                </td>
                <td class="report-chips">
                  <span class="chip" :class="checkPlayerPerformanceReportIsNotEmpty(session)
                    ? 'chip-done'
                    : 'chip-missing'">
                    You {{ checkPlayerPerformanceReportIsNotEmpty(session) ? "✓" : "–" }}
                  </span>
                  <span class="chip" :class="checkIfCoachReportExists(session.gameId)
                    ? 'chip-done'
                    : 'chip-missing'">
                    Coach {{ checkIfCoachReportExists(session.gameId) ? "✓" : "–" }}
                  </span>
                </td>
                <td class="action-col">
                  <b-button size="sm" variant="outline-primary" @click.stop="viewFullReport(session.gameId)">
                    View
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="player-game-report" ref="reportDetail" v-if="Object.keys(selectedGameReport).length > 0">
          <div class="report-header">
            <div class="report-headline">
              <h3>vs {{ selectedGameReport.opposition }}</h3>
              <p class="report-subline">
                {{ formatDateForHuman(selectedGameReport.matchDate) }}
                <template v-if="selectedGameReport.postion_played">
                  · {{ selectedGameReport.postion_played }}</template>
                <template v-if="selectedGameReport.postion_played_two">
                  / {{ selectedGameReport.postion_played_two }}</template>
              </p>
            </div>
            <b-button @click="downloadGameReportPDF" size="sm" variant="outline-primary" class="pdf-download-btn">
              <i class="bi bi-file-earmark-pdf"></i> Download PDF
            </b-button>
          </div>

          <div class="full-player-report">
            <div class="assessments-compare">
            <div class="player-assessment-report">
              <h4>Player Self Assessment</h4>
              <p class="small-date">
                Created:
                {{ formatDateForHuman(selectedGameReport.created_date) }}
              </p>
              <div class="small-image-name-wrapper">
                <img v-if="selectedUserData.userImage" class="small-image" :src="getImgUrl(selectedUserData.userImage)"
                  alt="Player Image" />
                <img v-else class="small-image" :src="getImgUrl('default-image.png')" alt="Default Image" />
                <span>{{ selectedUserData.name }}</span>
              </div>
              <p class="label">
                <span class="value">Goals:</span>
                {{ selectedGameReport.goals }}
              </p>
              <p class="label">
                <span class="value">Assists:</span>
                {{ selectedGameReport.assists }}
              </p>
              <p class="label">
                <span class="value">Card:</span>
                {{ selectedGameReport.card }}
              </p>
              <p class="label">
                <span class="value">Time on Pitch:</span>
                {{ selectedGameReport.timeOnPitch }} mins
              </p>
              <p v-if="selectedGameReport.positionPlayed === 'Goalkeeper'" class="label">
                <span class="value">Clean Sheet:</span>
                {{ selectedGameReport.cleanSheet }}
              </p>

              <div class="areas-wrapper" :class="{ 'not-complete': playerReportNotEmpty === false }">
                <p class="text-danger" v-if="playerReportNotEmpty === false">
                  <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
                  Player Self Assessment not complete
                </p>
                <div class="areas" v-for="(area, key) in activeAssessmentAreas" :key="key">
                  <h4>{{ area.title }}</h4>
                  <p class="label">
                    <span class="value">Positives:</span>
                  </p>
                  <p class="text">
                    {{ selectedGameReport[area.positivesKey] }}
                  </p>
                  <p class="label">
                    <span class="value">Improvements:</span>
                  </p>
                  <p class="text">
                    {{ selectedGameReport[area.improvementsKey] }}
                  </p>
                </div>
              </div>

              <b-button @click="openeditPlayerperformanceModal(selectedGameReport)" size="lg"
                variant="outline-success"><i class="bi bi-file-text" aria-hidden="true"></i> Edit Player
                Self Assessment</b-button>
            </div>

            <div class="coach-assessment-report" v-if="selectedGameReport.coachReportId !== undefined">
              <h4>Coach assessment</h4>

              <div class="small-image-name-wrapper">
                <img v-if="
                  selectedGameReport.userImage !== '' &&
                  selectedGameReport.userImage !== null
                " class="small-image" :src="getImgUrl(selectedGameReport.userImage)" alt="" />
                <img v-else class="small-image" :src="getImgUrl('default-image.png')" alt="" />
                <span>{{ selectedGameReport.coach_name }}</span>
              </div>

              <p class="label" v-if="
                userData.userType === 'Admin' || userData.userType === 'Coach'
              ">
                <span class="value">Coach rating:</span>
                {{
                  isStringNumberBetween0And5(selectedGameReport.player_rating)
                    ? convertCoachRatingToWords(
                      selectedGameReport.player_rating,
                    )
                    : selectedGameReport.player_rating
                }}
              </p>

              <p>
                <span class="value">Positive:</span>
                <span class="coach-comments">{{
                  selectedGameReport.game_comments
                  }}</span>
              </p>
              <p>
                <span class="value">Improvement:</span>
                <span class="coach-comments">{{
                  selectedGameReport.game_improvements
                  }}</span>
              </p>

              <b-button v-if="
                userData.userType === 'Admin' || userData.userType === 'Coach'
              " @click="
                openCoachGamePerformanceModal(selectedGameReport, 'Edit')
                " size="lg" variant="outline-success"><i class="bi bi-file-text" aria-hidden="true"></i> Edit coach
                Assessment</b-button>
            </div>
            <div v-else class="missing-coach-assessment-report">
              <h4>Coach assessment</h4>
              <div class="small-image-name-wrapper">
                <p class="text-danger">
                  <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
                  Not Completed
                </p>
                <img class="small-image" :src="getImgUrl('default-image.png')" alt="" />
              </div>

              <b-button v-if="
                userData.userType === 'Admin' || userData.userType === 'Coach'
              " @click="
                openCoachGamePerformanceModal(selectedGameReport, 'New')
                " size="lg" variant="outline-success"><i class="bi bi-file-text" aria-hidden="true"></i> Add coach
                Assessment</b-button>
            </div>
            </div>

            <div class="areas idp-game-targets" v-if="inPossessionTargets.length > 0 || outPossessionTargets.length > 0">
              <h4>IDPs — Game targets</h4>

              <template v-if="inPossessionTargets.length > 0">
                <p class="label"><span class="value">In possession:</span></p>
                <div class="text target-outline" v-for="gameTarget in inPossessionTargets"
                  :key="`${gameTarget.id}-Inpossession`">
                  <div class="target-wrapper">
                    <img src="./img/target.svg" alt="" />
                    <span>{{ stripHtml(gameTarget.target) }}</span>
                    <span v-if="gameTarget.complete === 0" class="not-complete">Not complete</span>
                    <span v-else class="complete">Complete</span>
                  </div>
                </div>
              </template>

              <template v-if="outPossessionTargets.length > 0">
                <p class="label" style="margin-top: 1rem">
                  <span class="value">Out of possession:</span>
                </p>
                <div class="text target-outline" v-for="gameTarget in outPossessionTargets"
                  :key="`${gameTarget.id}-Outofpossession`">
                  <div class="target-wrapper">
                    <img src="./img/target.svg" alt="" />
                    <span>{{ stripHtml(gameTarget.target) }}</span>
                    <span v-if="gameTarget.complete === 0" class="not-complete">Not complete</span>
                    <span v-else class="complete">Complete</span>
                  </div>
                </div>
              </template>
            </div>

            <!-- Eyeball Match Video & Team Lines -->
            <details v-if="eyeBallSelectedGameData.video || eyeBallSelectedGameData.homeGameTeam"
              class="collapse-section eyeball-match-section">
              <summary>
                <i class="bi bi-camera-video-fill"></i> Match video &amp; team lines
              </summary>

              <div class="eyeball-video-wrapper" v-if="eyeBallSelectedGameData.video?.url">
                <video ref="hlsVideoPlayer" controls width="100%" style="max-height: 400px"
                  :poster="eyeBallSelectedGameData.video?.poster">
                  Your browser does not support the video tag.
                </video>
              </div>

              <div class="eyeball-team-lines" v-if="eyeBallSelectedGameData.homeGameTeam || eyeBallSelectedGameData.awayGameTeam">
                <ul class="nav nav-tabs mb-3">
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: eyeballTeamSheetTab === 'homeTeam-tab' }"
                      @click="eyeballTeamSheetTab = 'homeTeam-tab'" href="javascript:;">
                      {{ eyeBallSelectedGameData.homeGameTeam?.club?.name || 'Home' }}
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: eyeballTeamSheetTab === 'awayTeam-tab' }"
                      @click="eyeballTeamSheetTab = 'awayTeam-tab'" href="javascript:;">
                      {{ eyeBallSelectedGameData.awayGameTeam?.club?.name || 'Away' }}
                    </a>
                  </li>
                </ul>

                <div v-if="eyeballTeamSheetTab === 'homeTeam-tab'">
                  <div v-if="eyeBallSelectedGameData.homeGameTeam?.players?.length > 0">
                    <h5>Starting XI</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.homeGameTeam.players.filter(p => !p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <h5>Substitutes</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.homeGameTeam.players.filter(p => p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p v-else>No home team line data</p>
                </div>

                <div v-if="eyeballTeamSheetTab === 'awayTeam-tab'">
                  <div v-if="eyeBallSelectedGameData.awayGameTeam?.players?.length > 0">
                    <h5>Starting XI</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.awayGameTeam.players.filter(p => !p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <h5>Substitutes</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.awayGameTeam.players.filter(p => p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p v-else>No away team line data</p>
                </div>
              </div>
            </details>

            <div v-if="isLoadingEyeballData" class="text-center py-3">
              <span class="spinner-border spinner-border-sm" role="status"></span> Loading Eyeball data...
            </div>
          </div>

          <details class="collapse-section gps-data-for-same-date" v-if="
            Object.keys(selectedGameFullPerformanceReportGPSdataForUser)
              .length > 0
          ">
            <summary><i class="bi bi-geo-alt-fill"></i> GPS data</summary>

            <div class="player-stats-gps-wrapper">
              <div class="wrapper-mins-stuff"></div>

              <div class="player-session-details">
                <div class="session-name">
                  <p>
                    <span>Session name:</span>
                    {{
                      selectedGameFullPerformanceReportGPSdataForUser.session_type
                    }}
                  </p>
                </div>
                <gps-pannel :selectedDayPlayerData="selectedGameFullPerformanceReportGPSdataForUser
                  " :selectedUserData="selectedUserData" :playerPlayMakerGpsData="playerPlayMakerGpsData" />
              </div>
            </div>
          </details>

          <!-- PlayerData GPS Section (PTFC Only) -->
          <details class="collapse-section playerdata-gps-section" v-if="userData && userData.clubId === 'ptfc-2085XH'">
            <summary>
              <span class="playerdata-badge">PlayerData</span> GPS performance metrics
            </summary>
            <div v-if="loadingPlayerDataGps" class="loading-state">
              <spinner-loader :loadingStuff="true" />
              <p>Loading PlayerData GPS metrics...</p>
            </div>

            <div v-else-if="playerDataGpsMetrics" class="playerdata-gps-data">

              <!-- Session Info -->
              <div class="session-meta-info">
                <span class="session-type" :class="playerDataGpsMetrics.isMatch ? 'match' : 'training'">
                  {{ playerDataGpsMetrics.sessionType }}
                </span>
                <span v-if="playerDataGpsMetrics.isMatch && playerDataGpsMetrics.opponent" class="match-info">
                  vs {{ playerDataGpsMetrics.opponent }}
                  <span class="score">{{ playerDataGpsMetrics.score }} - {{ playerDataGpsMetrics.opponentScore }}</span>
                </span>
              </div>

              <!-- Core Metrics Grid -->
              <div class="playerdata-metrics-grid">
                <div class="metric-card distance">
                  <div class="metric-icon"><i class="bi bi-rulers"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ formatPlayerDataDistance(playerDataGpsMetrics.totalDistance) }}</div>
                    <div class="metric-label">Total Distance</div>
                  </div>
                </div>

                <div class="metric-card speed">
                  <div class="metric-icon"><i class="bi bi-speedometer2"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ playerDataGpsMetrics.maxSpeed.toFixed(1) }} km/h</div>
                    <div class="metric-label">Top Speed</div>
                  </div>
                </div>

                <div class="metric-card sprint">
                  <div class="metric-icon"><i class="bi bi-lightning-fill"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ playerDataGpsMetrics.sprintEvents }}</div>
                    <div class="metric-label">Sprint Events</div>
                  </div>
                </div>

                <div class="metric-card hsr">
                  <div class="metric-icon"><i class="bi bi-activity"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ formatPlayerDataDistance(playerDataGpsMetrics.highIntensityDistance) }}
                    </div>
                    <div class="metric-label">High Intensity Running</div>
                  </div>
                </div>

                <div class="metric-card acceleration">
                  <div class="metric-icon"><i class="bi bi-graph-up-arrow"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ playerDataGpsMetrics.accelerationEvents }}</div>
                    <div class="metric-label">Accelerations</div>
                  </div>
                </div>

                <div class="metric-card deceleration">
                  <div class="metric-icon"><i class="bi bi-graph-down-arrow"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ playerDataGpsMetrics.decelerationEvents }}</div>
                    <div class="metric-label">Decelerations</div>
                  </div>
                </div>

                <div class="metric-card sprint-distance">
                  <div class="metric-icon"><i class="bi bi-wind"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ formatPlayerDataDistance(playerDataGpsMetrics.sprintDistance) }}</div>
                    <div class="metric-label">Sprint Distance</div>
                  </div>
                </div>

                <div class="metric-card pace">
                  <div class="metric-icon"><i class="bi bi-stopwatch"></i></div>
                  <div class="metric-content">
                    <div class="metric-value">{{ playerDataGpsMetrics.metresPerMinute.toFixed(1) }} m/min</div>
                    <div class="metric-label">Metres/Minute</div>
                  </div>
                </div>
              </div>

              <!-- Half Breakdown (for matches) -->
              <div
                v-if="playerDataGpsMetrics.isMatch && (playerDataGpsMetrics.firstHalfDistance > 0 || playerDataGpsMetrics.secondHalfDistance > 0)"
                class="half-breakdown">
                <h5>Distance by Half</h5>
                <div class="half-bars">
                  <div class="half-bar first-half">
                    <span class="half-label">1st Half</span>
                    <div class="bar-fill"
                      :style="{ width: getHalfPercentage(playerDataGpsMetrics.firstHalfDistance) + '%' }"></div>
                    <span class="half-value">{{ formatPlayerDataDistance(playerDataGpsMetrics.firstHalfDistance)
                    }}</span>
                  </div>
                  <div class="half-bar second-half">
                    <span class="half-label">2nd Half</span>
                    <div class="bar-fill"
                      :style="{ width: getHalfPercentage(playerDataGpsMetrics.secondHalfDistance) + '%' }"></div>
                    <span class="half-value">{{ formatPlayerDataDistance(playerDataGpsMetrics.secondHalfDistance)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Speed Zones -->
              <div v-if="playerDataGpsMetrics.speedZones" class="speed-zones-section">
                <h5>Speed Zone Distribution</h5>
                <div class="speed-zones-bar">
                  <div class="zone stationary" :style="{ width: playerDataGpsMetrics.speedZones.stationary + '%' }"
                    :title="'Stationary: ' + playerDataGpsMetrics.speedZones.stationary.toFixed(1) + '%'"></div>
                  <div class="zone jogging" :style="{ width: playerDataGpsMetrics.speedZones.jogging + '%' }"
                    :title="'Jogging: ' + playerDataGpsMetrics.speedZones.jogging.toFixed(1) + '%'"></div>
                  <div class="zone low-intensity" :style="{ width: playerDataGpsMetrics.speedZones.lowIntensity + '%' }"
                    :title="'Low Intensity: ' + playerDataGpsMetrics.speedZones.lowIntensity.toFixed(1) + '%'"></div>
                  <div class="zone medium-intensity"
                    :style="{ width: playerDataGpsMetrics.speedZones.mediumIntensity + '%' }"
                    :title="'Medium Intensity: ' + playerDataGpsMetrics.speedZones.mediumIntensity.toFixed(1) + '%'">
                  </div>
                  <div class="zone high-intensity"
                    :style="{ width: playerDataGpsMetrics.speedZones.highIntensity + '%' }"
                    :title="'High Intensity: ' + playerDataGpsMetrics.speedZones.highIntensity.toFixed(1) + '%'"></div>
                  <div class="zone sprint" :style="{ width: playerDataGpsMetrics.speedZones.sprint + '%' }"
                    :title="'Sprint: ' + playerDataGpsMetrics.speedZones.sprint.toFixed(1) + '%'"></div>
                </div>
                <div class="speed-zones-legend">
                  <span class="legend-item"><span class="dot stationary"></span>Stationary</span>
                  <span class="legend-item"><span class="dot jogging"></span>Jogging</span>
                  <span class="legend-item"><span class="dot low-intensity"></span>Low</span>
                  <span class="legend-item"><span class="dot medium-intensity"></span>Medium</span>
                  <span class="legend-item"><span class="dot high-intensity"></span>High</span>
                  <span class="legend-item"><span class="dot sprint"></span>Sprint</span>
                </div>
              </div>
            </div>

            <div v-else-if="!loadingPlayerDataGps && selectedGameReport.matchDate" class="no-playerdata-gps">
              <p class="info-text">
                <i class="bi bi-info-circle"></i>
                No PlayerData GPS session found for this match date.
              </p>
            </div>
          </details>
        </div>

        <div v-else-if="sortedSeasonReports.length > 0" class="full-player-report">
          <div class="empty-state">
            <img src="./img/emptyStateIllustration.svg" alt="" />
            <p>Select a game from the list to view its report.</p>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showEditPlayerperformanceModal" v-model="showEditPlayerperformanceModal" size="lg"
      title="Edit your game performance" :hide-footer="Boolean(true)">
      <form class="form performance-form" @submit.prevent="updateUserGamePerformanceReport()">
        <section class="pf-card">
          <h3 class="pf-card__title">Match details</h3>
          <div class="pf-grid">
            <div class="form__item">
              <label class="form__label" for="pf_position">Position played</label>
              <select id="pf_position" v-model="editGamePerformanceData.postion_played">
                <option disabled value="">Please select a position</option>
                <option v-for="position in positionOptions" :key="position" :value="position">
                  {{ position }}
                </option>
              </select>
            </div>

            <div class="form__item">
              <label class="form__label" for="pf_position_two">Second position (optional)</label>
              <select id="pf_position_two" v-model="editGamePerformanceData.postion_played_two">
                <option value="">None</option>
                <option v-for="position in positionOptions" :key="position" :value="position">
                  {{ position }}
                </option>
              </select>
            </div>

            <div class="form__item">
              <label class="form__label" for="pf_timeOnPitch">Minutes played</label>
              <input class="form__input" type="number" id="pf_timeOnPitch" max="120" min="0" inputmode="numeric"
                v-model="editGamePerformanceData.timeOnPitch" />
            </div>

            <div v-if="editGamePerformanceData.postion_played === 'Goalkeeper'" class="form__item">
              <label class="form__label" for="pf_clean_sheet">Clean sheet?</label>
              <select id="pf_clean_sheet" v-model="editGamePerformanceData.clean_sheet">
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>
        </section>

        <!-- NOTE: Goals, assists, and cards are tracked by coaches only via team_game_results -->

        <section class="pf-card" v-for="area in activeAssessmentAreas" :key="area.title">
          <h3 class="pf-card__title">{{ area.title }}</h3>
          <p class="pf-card__hint">{{ area.hint }}</p>

          <div class="pf-grid pf-grid--two">
            <div class="form__item">
              <div class="pf-field-head">
                <label class="form__label" :for="`pf_${area.positivesKey}`">{{ area.simple ? 'Positive' : 'What went well' }}</label>
                <VoiceDictationButton v-model="editGamePerformanceData[area.positivesKey]" label="Dictate" />
              </div>
              <b-form-textarea :id="`pf_${area.positivesKey}`"
                v-model="editGamePerformanceData[area.positivesKey]"
                :placeholder="area.simple ? 'What went well this game…' : `${area.title} — what went well…`" rows="3" max-rows="6"></b-form-textarea>
            </div>

            <div class="form__item">
              <div class="pf-field-head">
                <label class="form__label" :for="`pf_${area.improvementsKey}`">{{ area.simple ? 'Improvement' : 'What to improve' }}</label>
                <VoiceDictationButton v-model="editGamePerformanceData[area.improvementsKey]" label="Dictate" />
              </div>
              <b-form-textarea :id="`pf_${area.improvementsKey}`"
                v-model="editGamePerformanceData[area.improvementsKey]"
                :placeholder="area.simple ? 'One thing to work on next time…' : `${area.title} — what to work on…`" rows="3" max-rows="6"></b-form-textarea>
            </div>
          </div>
        </section>

        <section class="pf-card" v-if="!isGrassrootsTenant">
          <div class="pf-field-head">
            <label class="form__label" for="pf_overall">Overall comments</label>
            <VoiceDictationButton v-model="editGamePerformanceData.overall_comments" label="Dictate" />
          </div>
          <b-form-textarea id="pf_overall" v-model="editGamePerformanceData.overall_comments"
            placeholder="Anything else worth remembering about this game…" rows="3" max-rows="6"></b-form-textarea>
        </section>

        <div class="pf-actions">
          <p v-if="$store.getters.isReadOnlySeason" class="pf-readonly-note">
            <i class="bi bi-lock" aria-hidden="true"></i>
            This season is read-only — the report can't be changed.
          </p>
          <b-button type="submit" size="lg" variant="success" :disabled="$store.getters.isReadOnlySeason">
            Save game performance
          </b-button>
        </div>
      </form>
    </b-modal>

    <b-modal v-if="showNewEditcoachGamePerformanceModal" v-model="showNewEditcoachGamePerformanceModal"
      title="Record coaches match report assesment" size="lg" :hide-footer="Boolean(true)"
      @hide="hideCoachPerformanceModel()">
      <form class="form" @submit.prevent="coachesGamePerformanceReport()">
        <div class="small-image-name-wrapper player-coach-performance-image">
          <img v-if="selectedUserData.userImage" class="small-image" :src="getImgUrl(selectedUserData.userImage)"
            alt="Player Image" />
          <img v-else class="small-image" :src="getImgUrl('default-image.png')" alt="Default Image" />
          <span><span class="user-name">{{ selectedUserData.name }}</span>
            <br />match report for
            {{ formatDateForHuman(selectedGameReport.matchDate) }} Vs
            {{ selectedGameReport.opposition }}</span>
        </div>

        <div v-if="eyeballPrefillApplied" class="alert alert-info py-2 px-3 mb-3" style="font-size: 0.9em;">
          <i class="bi bi-info-circle"></i>
          Pre-filled minutes, goals and position from Eyeball team lines (won't overwrite existing data).
        </div>

        <div class="form__item">
          <label class="form__label" for="postion_played">What position did they play?</label>
          <select id="postion_played" v-model="selectedCoachGamePerformanceData.postion_played">
            <option disabled value="">Please select a position</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">Right Full-back (Wingback)</option>
            <option value="Left Full-back (Wingback)">Left Full-back (Wingback)</option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">Centre Defensive Midfield</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Right Midfielder (Winger)">Right Midfielder (Winger)</option>
            <option value="Left Midfielder (Winger)">Left Midfielder (Winger)</option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">Center Forward (Striker) left</option>
            <option value="Center Forward (Striker) right">Center Forward (Striker) right</option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="postion_played_two">Second position played?</label>
          <select id="postion_played_two" v-model="selectedCoachGamePerformanceData.postion_played_two">
            <option value="">None</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">Right Full-back (Wingback)</option>
            <option value="Left Full-back (Wingback)">Left Full-back (Wingback)</option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">Centre Defensive Midfield</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Right Midfielder (Winger)">Right Midfielder (Winger)</option>
            <option value="Left Midfielder (Winger)">Left Midfielder (Winger)</option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">Center Forward (Striker) left</option>
            <option value="Center Forward (Striker) right">Center Forward (Striker) right</option>
          </select>
        </div>

        <div class="coach-stats-row">
          <div class="form__item">
            <label class="form__label" for="session_mins">Playing Time (mins)</label>
            <b-form-input id="session_mins" v-model="selectedCoachGamePerformanceData.session_mins" type="number"
              min="0" placeholder="0"></b-form-input>
          </div>

          <div class="form__item">
            <label class="form__label" for="assists">Assists</label>
            <b-form-input id="assists" v-model="selectedCoachGamePerformanceData.assists" type="number" min="0"
              placeholder="0"></b-form-input>
          </div>

          <div class="form__item">
            <label class="form__label" for="goals">Goals</label>
            <b-form-input id="goals" v-model="selectedCoachGamePerformanceData.goals" type="number" min="0"
              placeholder="0"></b-form-input>
          </div>

          <div class="form__item">
            <label class="form__label">Cards</label>
            <div class="card-checkboxes">
              <b-form-checkbox v-model="selectedCoachGamePerformanceData.yellow_card" :value="true"
                :unchecked-value="false" class="yellow-card-checkbox">
                <span class="card-icon yellow">🟨</span> Yellow
              </b-form-checkbox>
              <b-form-checkbox v-model="selectedCoachGamePerformanceData.red_card" :value="true"
                :unchecked-value="false" class="red-card-checkbox">
                <span class="card-icon red">🟥</span> Red
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="form__item">
          <label class="form__label" for="player_rating">How did you think<br />
            {{ selectedUserData.name }} played?</label>
          <select id="player_rating" v-model="selectedCoachGamePerformanceData.player_rating">
            <option value="">Please select a rating</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Progressing well">Progressing well</option>
            <option value="⁠⁠Requires improvement">⁠⁠Requires improvement</option>
            <option value="Unsatisfactory">Unsatisfactory</option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="game_comments">Positive comments</label>
          <div style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:6px;">
            <!-- iOS Safari note: do NOT use .prevent on this @click. Calling
                 event.preventDefault() in a click handler invalidates the
                 "user activation" context that SpeechRecognition.start()
                 requires on iOS, causing the mic button to silently no-op.
                 type="button" already prevents form submission, so .prevent
                 is unnecessary anyway. -->
            <button class="btn btn-outline-danger btn-sm voice-record-button" type="button"
              @click="toggleAIVoiceRecording('positives')">
              <i :class="`bi ${aiIsRecording === 'positives' ? 'bi-mic-fill' : 'bi-mic'}`" aria-hidden="true"></i>
              {{ aiIsRecording === "positives" ? "Stop Recording" : "Record Voice" }}
            </button>
            <button class="btn btn-outline-primary btn-sm" type="button"
              :disabled="aiRefineLoading === 'positives' || !selectedCoachGamePerformanceData.game_comments"
              @click="refineField('positives')">
              <span v-if="aiRefineLoading === 'positives'">Refining…</span>
              <span v-else><i class="bi bi-stars"></i> Refine with AI</span>
            </button>
          </div>
          <textarea class="form__input" id="game_comments" rows="4"
            placeholder="Dictate or type what the player did well…"
            v-model="selectedCoachGamePerformanceData.game_comments"></textarea>
        </div>

        <div class="form__item">
          <label class="form__label" for="improvements_comments">Improvements comments</label>
          <div style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:6px;">
            <!-- See note above re: no .prevent on @click — iOS gesture context. -->
            <button class="btn btn-outline-danger btn-sm voice-record-button" type="button"
              @click="toggleAIVoiceRecording('improvements')">
              <i :class="`bi ${aiIsRecording === 'improvements' ? 'bi-mic-fill' : 'bi-mic'}`" aria-hidden="true"></i>
              {{ aiIsRecording === "improvements" ? "Stop Recording" : "Record Voice" }}
            </button>
            <button class="btn btn-outline-primary btn-sm" type="button"
              :disabled="aiRefineLoading === 'improvements' || !selectedCoachGamePerformanceData.game_improvements"
              @click="refineField('improvements')">
              <span v-if="aiRefineLoading === 'improvements'">Refining…</span>
              <span v-else><i class="bi bi-stars"></i> Refine with AI</span>
            </button>
          </div>
          <textarea class="form__input" id="improvements_comments" rows="4"
            placeholder="Dictate or type areas for the player to work on…"
            v-model="selectedCoachGamePerformanceData.game_improvements"></textarea>
        </div>

        <div class="form__item" v-if="coachData.length > 0">
          <label class="form__label" for="coachSelect">Name of Coach(s) completing the report</label>

          <b-form-select v-model="selectedCoachGamePerformanceData.coachId" id="coachSelect" :state="coachSelectTouched
            ? selectedCoachGamePerformanceData.coachId
              ? true
              : false
            : null
            " required @blur="coachSelectTouched = true">
            <b-form-select-option disabled value="">Please select a coach</b-form-select-option>
            <b-form-select-option v-for="coach in coachData" :value="coach.id" :key="coach.id">
              {{ coach.name }}
            </b-form-select-option>
          </b-form-select>
          <b-form-invalid-feedback v-if="
            coachSelectTouched && !selectedCoachGamePerformanceData.coachId
          ">
            Please select a coach to complete the report
          </b-form-invalid-feedback>

          <p>
            Selected coach:
            <span v-if="selectedCoachGamePerformanceData.coachId !== null">{{
              getCoachesNameFromId(selectedCoachGamePerformanceData.coachId)
              }}</span>
            <span v-else>No coach selected!</span>
          </p>
        </div>

        <b-button type="submit" size="lg" variant="outline-success" :disabled="$store.getters.isReadOnlySeason"><i class="bi bi-file-text" aria-hidden="true"></i>
          Submit player game
          performance report</b-button>
      </form>
    </b-modal>
  </div>
</template>

<script>
import GpsPannel from "../../components/GpsPanel";
import SpinnerLoader from "../../components/LoadingSpinning";
import VoiceDictationButton from "@/components/VoiceDictationButton.vue";
import { Games } from "@/services/games-service.js";
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import { Users } from "@/services/user-service.js";
import { playerDataApi } from "@/utils/playerDataBackend.js";
import { PlayerData } from "@/services/playerData-service.js";
import { EyeBall } from "@/services/eyeball-service.js";
import { AI } from "@/services/ai-service.js";
import { getTier } from "@/config/tenant";
import Hls from "hls.js";
import JsPDF from "jspdf";
import { avatarUrl } from "@/utils/avatar";
export default {
  name: "GameReportsModal",
  emits: [
    "getCoachesGamePerformanceReport",
    "getThisSeasionsCoachesGamePerformanceReports",
    "sendFBNotificationMessage",
    "coachGamePerformanceModal",
    "loadPlayersProfilePerformanceData",
    "getAllGamesEventsForPlayer",
    "refreshSeasonsGamePerformanceReports",
  ],
  components: {
    GpsPannel,
    SpinnerLoader,
    VoiceDictationButton,
  },
  props: {
    seasonsGamePerformanceReports: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectedUserData: {
      type: Object,
      required: true,
    },
    userData: {
      type: Object,
    },
    playerGameTargetsData: {
      type: Array,
      default: () => [],
    },
    playerPlayMakerGpsData: {
      type: Array,
      default: () => [],
    },
    coachGamePerformanceData: {
      type: [Object, Array],
      default: () => [],
    },
  },
  data() {
    return {
      loadingStuff: false,
      showAllGameReportsModal: false,
      showEditPlayerperformanceModal: false,
      showNewEditcoachGamePerformanceModal: false,
      eyeballPrefillApplied: false,
      coachSelectTouched: false,
      aiIsRecording: null,
      aiRecognition: null,
      aiRefineLoading: null,
      selectedCoachGamePerformanceData: {},
      selectedGameFullPerformanceReportGPSdataForUser: {},
      selectedSeason: "current", // 'current' | 'last' | 'summary'
      summaryTimeline: "currentSeason",
      lastSeasonReports: [],
      lastSeasonCoachReports: [],
      loadingLastSeason: false,
      editGamePerformanceData: {
        postion_played: "",
        postion_played_two: "",
        technically_improvements: "",
        technically_positives: "",
        tactility_improvements: "",
        tactility_positives: "",
        physicality_improvements: "",
        physicality_positives: "",
        mentally_improvements: "",
        mentally_positives: "",
        overall_comments: "",
      },
      coachData: [],
      playerReportNotEmpty: false,
      selectedGameReport: {},
      assessmentAreas: [
        {
          title: "Technical",
          improvementsKey: "technically_improvements",
          positivesKey: "technically_positives",
          hint: "First touch, passing, shooting, ball control…",
        },
        {
          title: "Tactical",
          improvementsKey: "tactility_improvements",
          positivesKey: "tactility_positives",
          hint: "Positioning, decision-making, awareness, pressing…",
        },
        {
          title: "Physical",
          improvementsKey: "physicality_improvements",
          positivesKey: "physicality_positives",
          hint: "Pace, stamina, strength in duels, recovery runs…",
        },
        {
          title: "Mental",
          improvementsKey: "mentally_improvements",
          positivesKey: "mentally_positives",
          hint: "Focus, confidence, communication, reaction to setbacks…",
        },
      ],
      positionOptions: [
        "Goalkeeper",
        "Right Full-back (Wingback)",
        "Left Full-back (Wingback)",
        "Center-back left",
        "Center-back right",
        "Centre midfield",
        "Centre Defensive Midfield",
        "Defensive Midfielder",
        "Right Midfielder (Winger)",
        "Left Midfielder (Winger)",
        "Attacking Midfielder",
        "Center Forward (Striker) left",
        "Center Forward (Striker) right",
      ],
      // Eyeball Integration
      eyeBallTeamsGames: [],
      eyeBallSelectedGameData: {},
      showEyeballSection: false,
      isLoadingEyeballData: false,
      eyeballPlayerData: null,
      eyeballTeamSheetTab: "homeTeam-tab",
      hlsInstance: null,
      // PlayerData GPS Integration
      playerDataGpsMetrics: null,
      playerDataSessionId: null,
      loadingPlayerDataGps: false,
      playerDataSessions: [], // cached sessions list
      playerDataTeamId: null, // cached team ID
      playerDataTeams: [], // cached teams list from database
    };
  },
  computed: {
    // Grassroots clubs get a simplified self-assessment: a single Positive +
    // Improvement instead of the four Technical/Tactical/Physical/Mental areas.
    // Stored in the technically_* columns (reused as the general pair) so no
    // schema change is needed. Pro tenants keep the full four areas.
    isGrassrootsTenant() {
      return getTier() === "grassroots";
    },
    activeAssessmentAreas() {
      if (this.isGrassrootsTenant) {
        return [
          {
            title: "Self assessment",
            hint: "What went well, and one thing to work on next time?",
            simple: true,
            positivesKey: "technically_positives",
            improvementsKey: "technically_improvements",
          },
        ];
      }
      return this.assessmentAreas;
    },
    selectedSeasonLabel() {
      return this.selectedSeason === "current"
        ? this.getCurrentSeason().label
        : this.getLastSeason().label;
    },

    currentSeasonReports() {
      return this.selectedSeason === "current"
        ? this.seasonsGamePerformanceReports
        : this.lastSeasonReports;
    },
    sortedSeasonReports() {
      return [...this.currentSeasonReports].sort(
        (a, b) => new Date(b.matchDate) - new Date(a.matchDate),
      );
    },
    inPossessionTargets() {
      return (this.playerGameTargetsData || []).filter(
        (t) => t.possession === "In possession",
      );
    },
    outPossessionTargets() {
      return (this.playerGameTargetsData || []).filter(
        (t) => t.possession === "Out of possession",
      );
    },
    currentSeasonCoachReports() {
      return this.selectedSeason === "current"
        ? this.coachGamePerformanceData
        : this.lastSeasonCoachReports;
    },
    summaryStats() {
      // Get the appropriate reports based on timeline
      let playerReports = [];
      let coachReports = [];
      const now = new Date();

      // Combine all available reports
      const allPlayerReports = [
        ...this.seasonsGamePerformanceReports,
        ...this.lastSeasonReports,
      ];
      const allCoachReports = [
        ...(Array.isArray(this.coachGamePerformanceData) ? this.coachGamePerformanceData : []),
        ...this.lastSeasonCoachReports,
      ];

      // Filter based on timeline
      switch (this.summaryTimeline) {
        case "last30": {
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          playerReports = allPlayerReports.filter(r => new Date(r.matchDate) >= thirtyDaysAgo);
          coachReports = allCoachReports.filter(r => new Date(r.matchDate) >= thirtyDaysAgo);
          break;
        }
        case "last90": {
          const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          playerReports = allPlayerReports.filter(r => new Date(r.matchDate) >= ninetyDaysAgo);
          coachReports = allCoachReports.filter(r => new Date(r.matchDate) >= ninetyDaysAgo);
          break;
        }
        case "currentSeason":
          playerReports = [...this.seasonsGamePerformanceReports];
          coachReports = Array.isArray(this.coachGamePerformanceData) ? [...this.coachGamePerformanceData] : [];
          break;
        case "lastSeason":
          playerReports = [...this.lastSeasonReports];
          coachReports = [...this.lastSeasonCoachReports];
          break;
        case "allTime":
        default:
          playerReports = allPlayerReports;
          coachReports = allCoachReports;
          break;
      }

      // Calculate stats
      const totalGames = playerReports.length;
      let totalGoals = 0;
      let totalAssists = 0;
      let totalMinutes = 0;
      let yellowCards = 0;
      let redCards = 0;
      const positionsPlayed = {};
      let playerReportsComplete = 0;

      // Process player reports
      playerReports.forEach(report => {
        totalGoals += parseInt(report.goals) || 0;
        totalAssists += parseInt(report.assists) || 0;
        totalMinutes += parseInt(report.timeOnPitch) || 0;

        // Count positions
        if (report.postion_played) {
          positionsPlayed[report.postion_played] = (positionsPlayed[report.postion_played] || 0) + 1;
        }

        // Check if player report is complete
        const hasContent = this.assessmentAreas.some(area =>
          (report[area.improvementsKey] && report[area.improvementsKey].trim()) ||
          (report[area.positivesKey] && report[area.positivesKey].trim())
        );
        if (hasContent) playerReportsComplete++;
      });

      // Process coach reports for additional stats
      const ratings = { excellent: 0, good: 0, average: 0, belowAverage: 0, poor: 0 };
      coachReports.forEach(report => {
        // Get stats from coach reports (these override player stats)
        totalGoals = Math.max(totalGoals, parseInt(report.goals) || 0);
        totalAssists = Math.max(totalAssists, parseInt(report.assists) || 0);

        // Cards from coach reports
        if (report.yellow_card === true || report.yellow_card === 1 || report.yellow_card === "1") {
          yellowCards++;
        }
        if (report.red_card === true || report.red_card === 1 || report.red_card === "1") {
          redCards++;
        }

        // Count ratings
        const rating = report.player_rating?.toLowerCase() || "";
        if (rating === "excellent" || rating === "5") ratings.excellent++;
        else if (rating === "good" || rating === "4") ratings.good++;
        else if (rating === "Progressing well" || rating === "3") ratings.average++;
        else if (rating === "⁠⁠Requires improvement" || rating === "2") ratings.belowAverage++;
        else if (rating === "Unsatisfactory" || rating === "1") ratings.poor++;
      });

      // Recalculate totals from coach data where available
      let totalGoalsFromCoach = 0;
      let totalAssistsFromCoach = 0;
      let totalMinutesFromCoach = 0;
      coachReports.forEach(report => {
        totalGoalsFromCoach += parseInt(report.goals) || 0;
        totalAssistsFromCoach += parseInt(report.assists) || 0;
        totalMinutesFromCoach += parseInt(report.session_mins) || 0;
      });

      // Use coach data if available
      if (totalGoalsFromCoach > 0) totalGoals = totalGoalsFromCoach;
      if (totalAssistsFromCoach > 0) totalAssists = totalAssistsFromCoach;
      if (totalMinutesFromCoach > 0) totalMinutes = totalMinutesFromCoach;

      // Calculate percentages for ratings
      const totalRatings = ratings.excellent + ratings.good + ratings.average + ratings.belowAverage + ratings.poor;
      const ratingPercentages = {
        excellent: totalRatings > 0 ? (ratings.excellent / totalRatings * 100) : 0,
        good: totalRatings > 0 ? (ratings.good / totalRatings * 100) : 0,
        average: totalRatings > 0 ? (ratings.average / totalRatings * 100) : 0,
        belowAverage: totalRatings > 0 ? (ratings.belowAverage / totalRatings * 100) : 0,
        poor: totalRatings > 0 ? (ratings.poor / totalRatings * 100) : 0,
      };

      return {
        totalGames,
        totalGoals,
        totalAssists,
        totalMinutes,
        avgMinutesPerGame: totalGames > 0 ? Math.round(totalMinutes / totalGames) : 0,
        goalsPerGame: totalGames > 0 ? (totalGoals / totalGames).toFixed(2) : "0.00",
        assistsPerGame: totalGames > 0 ? (totalAssists / totalGames).toFixed(2) : "0.00",
        yellowCards,
        redCards,
        positionsPlayed,
        playerReportsComplete,
        totalCoachReports: coachReports.length,
        ratings,
        ratingPercentages,
      };
    },
  },
  methods: {
    toggleAIVoiceRecording(fieldType) {
      if (this.aiIsRecording === fieldType) {
        this.stopAIVoiceRecording();
      } else {
        this.startAIVoiceRecording(fieldType);
      }
    },
    startAIVoiceRecording(fieldType) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("Your browser does not support voice recording.");
        return;
      }

      const targetKey = fieldType === "positives" ? "game_comments" : "game_improvements";
      const recognition = new SpeechRecognition();
      recognition.lang = "en-UK";
      // CRITICAL: interimResults: true. iOS Safari discards the buffer
      // on .stop() after the first session — interim events deliver the
      // transcript as the user speaks, bypassing the buffer-discard bug.
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      // Snapshot the field value at start, so each interim onresult
      // replaces the recording's-output portion cleanly instead of
      // compounding earlier interim chunks.
      const aiBaseValue = this.selectedCoachGamePerformanceData[targetKey] || "";

      recognition.onresult = (event) => {
        try {
          let transcriptSoFar = "";
          for (let i = 0; i < event.results.length; i++) {
            transcriptSoFar += event.results[i][0].transcript;
          }
          transcriptSoFar = transcriptSoFar.trim();
          const isFinal = event.results[event.results.length - 1]?.isFinal;
          console.log(
            "[GameReportAI] onresult fired, isFinal:", isFinal,
            "transcript:", transcriptSoFar,
          );
          if (!transcriptSoFar) return;

          // Field mutation (NOT object replacement) to preserve reactivity.
          this.selectedCoachGamePerformanceData[targetKey] =
            `${aiBaseValue}${aiBaseValue ? " " : ""}${transcriptSoFar}`;
        } catch (err) {
          console.error("[GameReportAI] onresult handler threw:", err);
        }
      };
      recognition.onend = () => {
        if (this.aiIsRecording === fieldType) {
          this.aiIsRecording = null;
        }
      };
      recognition.onerror = (event) => {
        if (event.error === "not-allowed") {
          alert("Microphone access is denied. Please enable it in your browser settings.");
        } else {
          console.error("SpeechRecognition error:", event.error);
          alert("Voice recording error: " + event.error);
        }
        if (this.aiIsRecording === fieldType) {
          this.aiIsRecording = null;
        }
      };

      recognition.start();
      this.aiIsRecording = fieldType;
      this.aiRecognition = recognition;
    },
    stopAIVoiceRecording() {
      if (this.aiRecognition) {
        this.aiRecognition.stop();
        this.aiIsRecording = null;
      }
    },
    async refineField(fieldType) {
      const targetKey = fieldType === "positives" ? "game_comments" : "game_improvements";
      const rawText = this.selectedCoachGamePerformanceData[targetKey] || "";
      if (!rawText.trim()) {
        alert("Please dictate or type some text first, then click Refine.");
        return;
      }
      this.aiRefineLoading = fieldType;
      try {
        const d = this.selectedCoachGamePerformanceData || {};
        const payload = {
          action: "refineCoachReportText",
          field_type: fieldType,
          raw_text: rawText,
          player_name: this.selectedUserData?.name || "",
          position: [d.postion_played, d.postion_played_two].filter(Boolean).join(", "),
        };
        const response = await AI.find(payload);
        const data = response?.data;
        if (data?.error) {
          alert("AI refine failed: " + data.error);
          return;
        }
        if (data?.refused) {
          alert("The AI only helps with match-report feedback for this player. Please keep your notes focused on how the player performed in this match.");
          return;
        }
        if (data?.refined) {
          this.selectedCoachGamePerformanceData[targetKey] = data.refined;
        } else {
          alert("AI returned an unexpected response. Please try again.");
        }
      } catch (err) {
        console.error(err);
        alert("AI refine request failed. Please try again.");
      } finally {
        this.aiRefineLoading = null;
      }
    },
    async populateCoachesData() {
      const coachesData = {
        clubId: this.userData.clubId,
        action: "populateOnlyCoachesData",
      };

      const responce = await Users.find(coachesData).catch((error) => {
        console.log(error);
      });

      responce.data.length > 0 ? (this.coachData = responce.data) : [];
    },
    getCoachesNameFromId(id) {
      const coach = this.coachData.find((coach) => coach.id === id);
      return coach ? coach.name : "";
    },
    getCurrentSeason() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const isAfterAugust = now.getMonth() >= 7; // August is month 7 (0-indexed)

      return {
        startYear: isAfterAugust ? currentYear : currentYear - 1,
        endYear: isAfterAugust ? currentYear + 1 : currentYear,
        label: isAfterAugust
          ? `${currentYear}/${currentYear + 1}`
          : `${currentYear - 1}/${currentYear}`,
      };
    },
    getLastSeason() {
      const current = this.getCurrentSeason();
      return {
        startYear: current.startYear - 1,
        endYear: current.endYear - 1,
        label: `${current.startYear - 1}/${current.endYear - 1}`,
      };
    },
    getSeasonDateRange(season) {
      return {
        start: `${season.startYear}-08-01`,
        end: `${season.endYear}-08-01`,
      };
    },
    async switchSeason(season) {
      this.selectedSeason = season;

      // Hide summary view when switching to current/last season
      if (season === "current" || season === "last") {
        this.showSummaryView = false;
      }

      if (season === "last" && this.lastSeasonReports.length === 0) {
        await this.loadLastSeasonReports();
        await this.loadLastSeasonCoachReports();
      }

      if (season === "summary") {
        await this.loadSummaryData();
      } else {
        this.autoSelectLatestReport();
      }
    },
    async loadSummaryData() {
      this.showSummaryView = true;

      // Ensure we have last season data loaded for comprehensive summary
      if (this.lastSeasonReports.length === 0) {
        await this.loadLastSeasonReports();
        await this.loadLastSeasonCoachReports();
      }
    },
    updateSummaryData() {
      // summaryStats computed property will automatically recalculate based on summaryTimeline
      // This method is called when the timeline selector changes
    },
    async getThisSeasonsgamePerformanceReportsForUser(
      seasonStartDate,
      seasonEndDate,
    ) {
      // Prepare request data
      const userData = {
        user_id: this.selectedUserData.id,
        season_start_date: seasonStartDate,
        season_end_date: seasonEndDate,
        action: "getThisSeasonsgamePerformanceReportsForUser",
      };

      // Make API call with better error handling
      const response = await Games.find(userData);

      if (Array.isArray(response.data) && response.data.length > 0) {
        this.lastSeasonReports = response.data;
      }
    },
    async getThisSeasionsCoachesGamePerformanceReports(
      seasonStartDate,
      seasonEndDate,
    ) {
      const userData = {
        user_id: this.selectedUserData.id,
        season_start_date: seasonStartDate,
        season_end_date: seasonEndDate,
        action: "getThisSeasionsCoachesGamePerformanceReports",
      };
      const responce = await Games.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data !== null) {
        this.lastSeasonCoachReports = responce.data || [];
        // this.filterOutAllGamesWithACoachPerformanceReport(responce.data);
      }
    },
    async loadLastSeasonReports() {
      this.loadingLastSeason = true;

      try {
        const lastSeason = this.getLastSeason();
        const dateRange = this.getSeasonDateRange(lastSeason);

        this.getThisSeasonsgamePerformanceReportsForUser(
          dateRange.start,
          dateRange.end,
        );
      } catch (error) {
        console.error("Error loading last season reports:", error);
        this.$toast.error("Failed to load last season reports");
      } finally {
        this.loadingLastSeason = false;
      }
    },
    async loadLastSeasonCoachReports() {
      try {
        const lastSeason = this.getLastSeason();
        const dateRange = this.getSeasonDateRange(lastSeason);

        await this.getThisSeasionsCoachesGamePerformanceReports(
          dateRange.start,
          dateRange.end,
        );
      } catch (error) {
        console.error("Error loading last season coach reports:", error);
      }
    },

    openAllGamegReportsModal() {
      this.loadingStuff = true;

      // reset data
      this.selectedCoachGamePerformanceData = {};
      this.selectedGameFullPerformanceReportGPSdataForUser = {};
      this.selectedGameReport = {};

      // Reset PlayerData GPS state
      this.playerDataGpsMetrics = null;
      this.loadingPlayerDataGps = false;

      // Reset Eyeball state and load games list
      this.eyeBallSelectedGameData = {};
      this.eyeballPlayerData = null;
      this.showEyeballSection = false;
      this.destroyHLSPlayer();
      this.loadEyeballTeamsGames();

      this.$emit("loadPlayersProfilePerformanceData");
      this.$emit("getCoachesGamePerformanceReport");
      this.$emit("getThisSeasionsCoachesGamePerformanceReports");

      // Emit the new event to refresh parent data
      this.$emit("refreshSeasonsGamePerformanceReports");

      this.showAllGameReportsModal = true;
      this.loadingStuff = false;
      this.$nextTick(() => this.autoSelectLatestReport());
    },
    async openCoachGamePerformanceModal(coachAssesment, type) {
      if (type === "Edit") {
        // Look in the season currently being viewed — the coach report for a
        // last-season game lives in lastSeasonCoachReports, not the
        // current-season prop. Guarded so a missing report edits as blank
        // rather than crashing.
        const coachReport =
          this.currentSeasonCoachReports.find(
            (report) => report.gameId === coachAssesment.gameId,
          ) || {};
        // Normalize player_rating to match select option values
        let rating =
          coachReport && coachReport.player_rating
            ? coachReport.player_rating
            : "";
        const validRatings = [
          "Excellent",
          "Good",
          "Progressing well",
          "⁠⁠Requires improvement",
          "Unsatisfactory",
        ];
        // Handle "Not Rated", "Not rated", or "not rated" as empty to show placeholder
        if (rating === "Not Rated" || rating === "Not rated" || rating === "not rated" || rating.toLowerCase() === "not rated") {
          rating = "";
        } else if (!validRatings.includes(rating)) {
          // Try to map numeric or alternate values to option strings
          const numToWord = {
            1: "Unsatisfactory",
            2: "⁠⁠Requires improvement",
            3: "Progressing well",
            4: "Good",
            5: "Excellent",
          };
          if (numToWord[rating]) rating = numToWord[rating];
          else rating = "";
        }

        // Parse positions_played from database (comma-separated) into individual fields
        let postionPlayed = coachReport.postion_played || "";
        let postionPlayedTwo = coachReport.postion_played_two || "";
        if (coachReport.positions_played && typeof coachReport.positions_played === 'string') {
          const positions = coachReport.positions_played.split(',');
          postionPlayed = positions[0] || "";
          postionPlayedTwo = positions[1] || "";
        }

        this.selectedCoachGamePerformanceData = {
          ...coachReport,
          gameId: coachReport.gameId !== undefined ? coachReport.gameId : coachAssesment.gameId,
          player_rating: rating,
          user_id: this.selectedUserData.id,
          session_mins: parseInt(coachReport.session_mins) || 0,
          assists: parseInt(coachReport.assists) || 0,
          goals: parseInt(coachReport.goals) || 0,
          yellow_card: coachReport.yellow_card === true || coachReport.yellow_card === 1 || coachReport.yellow_card === "1",
          red_card: coachReport.red_card === true || coachReport.red_card === 1 || coachReport.red_card === "1",
          postion_played: postionPlayed,
          postion_played_two: postionPlayedTwo,
        };
      } else {
        this.selectedCoachGamePerformanceData = {
          player_rating: "",
          game_comments: "",
          game_improvements: "",
          gameId: coachAssesment.gameId,
          coachId: this.userData.id,
          created_date: new Date().toISOString().split("T")[0],
          user_id: this.selectedUserData.id,
          session_mins: 0,
          assists: 0,
          goals: 0,
          yellow_card: false,
          red_card: false,
          postion_played: "",
          postion_played_two: "",
        };
      }

      await this.populateCoachesData();

      // Eyeball prefill: auto-fetch for this game's date, fill empty fields only
      this.eyeballPrefillApplied = false;
      const matchDate = coachAssesment?.matchDate;
      if (matchDate && this.userData?.clubId && this.selectedUserData?.id) {
        try {
          if (!this.eyeBallTeamsGames || this.eyeBallTeamsGames.length === 0) {
            await this.loadEyeballTeamsGames();
          }
          const eyeballGame = this.eyeBallTeamsGames.find((g) => g.date === matchDate);
          if (eyeballGame) {
            const response = await EyeBall.find({
              action: "getEyeballDataForMatchReport",
              matchDate: matchDate,
              clubId: this.userData.clubId,
            });
            if (response?.data?.success) {
              const playerId = String(this.selectedUserData.id);
              const eyeballForPlayer = response.data.playerData?.[playerId] || null;
              if (eyeballForPlayer) {
                const hasValue = (v) => v !== null && v !== undefined && v !== "" && v !== 0 && v !== "0";
                const d = this.selectedCoachGamePerformanceData;
                if (!hasValue(d.session_mins) && hasValue(eyeballForPlayer.minutes)) {
                  d.session_mins = eyeballForPlayer.minutes;
                }
                if (!hasValue(d.goals) && hasValue(eyeballForPlayer.goals)) {
                  d.goals = eyeballForPlayer.goals;
                }
                if (!hasValue(d.postion_played) && eyeballForPlayer.position) {
                  d.postion_played = eyeballForPlayer.position;
                }
                this.eyeballPrefillApplied = true;
              }
            }
          }
        } catch (err) {
          console.error("Eyeball prefill (GameReportsModal) failed:", err);
        }
      }

      this.showNewEditcoachGamePerformanceModal = true;
    },
    async coachesGamePerformanceReport() {
      const userData = {
        ...this.selectedCoachGamePerformanceData,
        coachId: this.selectedCoachGamePerformanceData.coachId,
        player_id: this.selectedUserData.id,
        created_date: new Date().toISOString().split("T")[0],
        action: "saveCoachesGamePerformanceReport",
      };
      const responce = await Games.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        // Update the local selectedGameReport with the new coach data
        this.updateSelectedGameReportWithCoachData();

        // Also refresh the data from the database to ensure we have the latest
        await this.refreshSelectedGameReportCoachData();

        this.$emit("getCoachesGamePerformanceReport");
        this.$emit("getThisSeasionsCoachesGamePerformanceReports");
        this.$emit(
          "sendFBNotificationMessage",
          "Coach added a game performance report",
          "You have a new game performance report to review.",
          [],
          this.selectedUserData.id,
        );

        this.$emit("getAllGamesEventsForPlayer");

        this.showNewEditcoachGamePerformanceModal = false;
        this.showAllGameReportsModal = false;

        this.$toast.info("Game Performance Report added", {
          title: "Saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    updateSelectedGameReportWithCoachData() {
      // Update the selectedGameReport with the newly saved coach data
      this.selectedGameReport = {
        ...this.selectedGameReport,
        coachReportId: this.selectedCoachGamePerformanceData.id || "new",
        coach_name: this.userData.name || "",
        coachId: this.selectedCoachGamePerformanceData.coachId,
        game_comments:
          this.selectedCoachGamePerformanceData.game_comments || "",
        game_improvements:
          this.selectedCoachGamePerformanceData.game_improvements || "",
        player_rating:
          this.selectedCoachGamePerformanceData.player_rating || "Not Rated",
        userImage: this.userData.userImage || "",
      };
    },
    async refreshSelectedGameReportCoachData() {
      // Refresh coach data for the currently selected game report
      if (this.selectedGameReport && this.selectedGameReport.id) {
        try {
          const response = await Games.find({
            action: "getCoachGamePerformanceReportsByGameId",
            gameId: this.selectedGameReport.gameId,
          });

          if (
            response.status === 200 &&
            response.data &&
            response.data.length > 0
          ) {
            const coachData = response.data[0];
            // Update the selected game report with fresh coach data
            this.selectedGameReport = {
              ...this.selectedGameReport,
              coachReportId: coachData.id,
              coach_name: coachData.coach_name || "",
              coachId: coachData.coachId,
              game_comments: coachData.game_comments || "",
              game_improvements: coachData.game_improvements || "",
              player_rating: coachData.player_rating || "Not Rated",
              userImage: coachData.userImage || "",
            };
          }
        } catch (error) {
          console.error("Error refreshing coach data:", error);
        }
      }
    },
    isStringNumberBetween0And5(str) {
      const num = Number(str);
      return !isNaN(num) && num >= 0 && num <= 5;
    },
    convertCoachRatingToWords(rating) {
      const isBetween0And5 = this.isStringNumberBetween0And5(rating);

      if (isBetween0And5 === true) {
        const ratings = {
          1: "Unsatisfactory",
          2: "⁠⁠Requires improvement",
          3: "Progressing well",
          4: "Good",
          5: "Excellent",
        };
        return ratings[rating] || "Not Rated";
      } else {
        return rating;
      }
    },
    async loadPlayerMinsPerformanceDataForDate(gameId, matchDate) {
      const data = {
        action: "getPlayerMinsPerformanceDataForDate",
        player_id: this.selectedUserData.id,
        session_date: matchDate,
      };

      const responce = await PlayerPhysicalPerformance.find(data).catch(
        (error) => {
          console.log(error);
        },
      );

      if (responce.status === 200) {
        this.selectedGameFullPerformanceReportGPSdataForUser = responce.data;
      }
    },

    /**
     * Eyeball Integration Methods
     */
    getEyeballTeamId() {
      if (this.userData.clubId === "ptfc-2085XH") return 8733;
      if (this.userData.clubId === "stjfc-2085XH") return 61829;
      return null;
    },

    async loadEyeballTeamsGames() {
      const teamIdValue = this.getEyeballTeamId();
      if (!teamIdValue) return;
      if (this.eyeBallTeamsGames.length > 0) return; // already loaded

      try {
        const response = await EyeBall.find({
          action: "getEyeBallTeamsGames",
          teamId: teamIdValue,
        });
        if (response && response.status === 200) {
          this.eyeBallTeamsGames = response.data.games || [];
        }
      } catch (error) {
        console.error("Error fetching Eyeball games:", error);
      }
    },

    async loadEyeballGameData(matchDate) {
      if (!this.eyeBallTeamsGames || this.eyeBallTeamsGames.length === 0) return;

      const eyeballGame = this.eyeBallTeamsGames.find((g) => g.date === matchDate);
      if (!eyeballGame) {
        this.eyeBallSelectedGameData = {};
        this.eyeballPlayerData = null;
        return;
      }

      this.isLoadingEyeballData = true;
      try {
        const response = await EyeBall.find({
          action: "getEyeBallGameById",
          gameId: eyeballGame.id,
        });

        if (response && response.status === 200) {
          this.eyeBallSelectedGameData = response.data;
          this.$nextTick(() => { this.initializeHLSPlayer(); });
        }

        // Also fetch mapped player data
        const playerDataResponse = await EyeBall.find({
          action: "getEyeballDataForMatchReport",
          matchDate: matchDate,
          clubId: this.userData.clubId,
        });

        if (playerDataResponse?.data?.success) {
          const playerId = String(this.selectedUserData.id);
          this.eyeballPlayerData = playerDataResponse.data.playerData[playerId] || null;
        }
      } catch (error) {
        console.error("Error fetching Eyeball game data:", error);
      } finally {
        this.isLoadingEyeballData = false;
      }
    },

    initializeHLSPlayer() {
      const video = this.$refs.hlsVideoPlayer;
      const videoUrl = this.eyeBallSelectedGameData.video?.url;
      const posterUrl = this.eyeBallSelectedGameData.video?.poster;

      if (!video || !videoUrl) return;

      if (posterUrl && !video.poster) {
        video.poster = posterUrl;
      }

      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR: hls.startLoad(); break;
              case Hls.ErrorTypes.MEDIA_ERROR: hls.recoverMediaError(); break;
              default: hls.destroy(); break;
            }
          }
        });
        this.hlsInstance = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoUrl;
      }
    },

    destroyHLSPlayer() {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    },

    formatMinutesFromMilliseconds(milliseconds) {
      if (!milliseconds || milliseconds === 0) return "0:00";
      const totalMinutes = Math.floor(milliseconds / 60000);
      const seconds = Math.floor((milliseconds % 60000) / 1000);
      return `${totalMinutes}:${seconds.toString().padStart(2, "0")}`;
    },

    /**
     * Load PlayerData GPS metrics for a specific match date and player
     * Uses fuzzy name matching to find the player in PlayerData sessions
     */
    async loadPlayerDataGpsForMatch(matchDate, playerName) {
      // Only available for PTFC club
      if (this.userData?.clubId !== 'ptfc-2085XH') {
        return;
      }

      this.loadingPlayerDataGps = true;
      this.playerDataGpsMetrics = null;

      try {
        // Get teams from local database (same as GpsReports page)
        if (!this.playerDataTeams || this.playerDataTeams.length === 0) {
          const teamsResponse = await PlayerData.find({
            playerId: this.userData.id,
            action: "getAllClubTeamsFromPlayerData",
          });

          if (teamsResponse.data?.data?.length > 0) {
            this.playerDataTeams = teamsResponse.data.data;
          } else {
            console.warn('No PlayerData teams found in database');
            this.loadingPlayerDataGps = false;
            return;
          }
        }

        // Find session matching the match date across all teams
        const matchDateObj = new Date(matchDate);
        const matchDateStr = matchDateObj.toISOString().split('T')[0];

        let matchingSession = null;
        let sessionsForTeam = [];

        // Search through each team for a session on the match date
        for (const team of this.playerDataTeams) {
          try {
            const sessionsResponse = await playerDataApi.getTeamSessions(team.id);

            if (sessionsResponse.success && sessionsResponse.data?.team?.sessions) {
              const sessions = sessionsResponse.data.team.sessions;

              // Find session matching the date
              const found = sessions.find(session => {
                if (!session.startTime) return false;
                const sessionDateStr = new Date(session.startTime).toISOString().split('T')[0];
                return sessionDateStr === matchDateStr;
              });

              if (found) {
                matchingSession = found;
                sessionsForTeam = sessions;
                this.playerDataTeamId = team.id;
                console.log('Found matching session in team:', team.name, 'Session:', found);
                break;
              }
            }
          } catch (err) {
            console.warn('Error fetching sessions for team:', team.name, err);
          }
        }

        if (!matchingSession) {
          console.log('No PlayerData session found for date:', matchDateStr, 'across', this.playerDataTeams.length, 'teams');
          this.loadingPlayerDataGps = false;
          return;
        }

        // Cache sessions for the found team
        this.playerDataSessions = sessionsForTeam;
        this.playerDataSessionId = matchingSession.id;

        // Get athlete metrics for this session
        const metricsResponse = await playerDataApi.getAthleteMetrics(matchingSession.id);

        if (metricsResponse.success && metricsResponse.data?.participations) {
          // Find the matching athlete using fuzzy name matching
          const athleteMetrics = this.findAthleteByFuzzyName(
            metricsResponse.data.participations,
            playerName
          );

          if (athleteMetrics) {
            this.playerDataGpsMetrics = this.processPlayerDataMetrics(athleteMetrics, matchingSession);
          } else {
            console.log('No matching athlete found for:', playerName);
          }
        }
      } catch (error) {
        console.error('Error loading PlayerData GPS:', error);
      }

      this.loadingPlayerDataGps = false;
    },

    /**
     * Find athlete in participations using fuzzy name matching
     */
    findAthleteByFuzzyName(participations, targetName) {
      if (!participations || !targetName) return null;

      const normalizedTarget = targetName.toLowerCase().trim();
      const targetParts = normalizedTarget.split(/\s+/);

      // Try exact match first
      let match = participations.find(p =>
        p.athlete?.name?.toLowerCase().trim() === normalizedTarget
      );
      if (match) return match;

      // Try matching first name + last name in any order
      match = participations.find(p => {
        const athleteName = p.athlete?.name?.toLowerCase().trim() || '';
        const athleteParts = athleteName.split(/\s+/);

        // Check if all target name parts appear in athlete name
        return targetParts.every(part =>
          athleteParts.some(ap => ap.includes(part) || part.includes(ap))
        );
      });
      if (match) return match;

      // Try partial first name or last name match
      match = participations.find(p => {
        const athleteName = p.athlete?.name?.toLowerCase().trim() || '';
        return targetParts.some(part => athleteName.includes(part) && part.length > 2);
      });

      return match;
    },

    /**
     * Process PlayerData metrics into a display-friendly format
     */
    processPlayerDataMetrics(participation, session) {
      // Handle different metric structures for Match vs Training sessions
      let metricSet = {};

      if (participation.__typename === 'MatchSessionParticipation') {
        metricSet = participation.metricSet || {};
      } else if (participation.__typename === 'TrainingSessionParticipation') {
        metricSet = participation.athleteMetricSet || {};
      } else {
        // Fallback
        metricSet = participation.metricSet || participation.athleteMetricSet || {};
      }

      const periodMetrics = participation.periodMetricSets || [];
      const firstHalf = periodMetrics[0] || {};
      const secondHalf = periodMetrics[1] || {};

      // Process speed zones
      const speedZones = this.processPlayerDataSpeedZones(metricSet.timeAtKph || []);

      return {
        athleteId: participation.athlete?.id,
        athleteName: participation.athlete?.name,
        sessionId: session.id,
        sessionType: session.__typename || 'Session',
        sessionDate: session.startTime,
        // Core metrics
        totalDistance: metricSet.totalDistanceM || 0,
        sprintDistance: metricSet.totalSprintDistanceM || 0,
        highIntensityDistance: metricSet.totalHighIntensityDistanceM || 0,
        maxSpeed: metricSet.maxSpeedKph || 0,
        avgSpeed: metricSet.avgSpeedKph || 0,
        sprintEvents: metricSet.sprintEvents || 0,
        metresPerMinute: metricSet.metresPerMinute || 0,
        accelerationEvents: metricSet.accelerationEvents || 0,
        decelerationEvents: metricSet.decelerationEvents || 0,
        // Half breakdown (for matches)
        firstHalfDistance: firstHalf.totalDistanceM || 0,
        secondHalfDistance: secondHalf.totalDistanceM || 0,
        // Speed zones
        speedZones,
        // Match info if available
        isMatch: session.__typename === 'MatchSession',
        opponent: session.opponent,
        score: session.score,
        opponentScore: session.opponentScore,
        result: session.result,
      };
    },

    /**
     * Process speed zones from timeAtKph array
     */
    processPlayerDataSpeedZones(timeAtKphArray) {
      const zones = {
        stationary: 0,      // 0 - 8.47 km/h
        jogging: 0,         // 8.47 - 12.7 km/h  
        lowIntensity: 0,    // 12.7 - 16.94 km/h
        mediumIntensity: 0, // 16.94 - 23.29 km/h
        highIntensity: 0,   // 23.29 - 30.49 km/h
        sprint: 0,          // 30.49+ km/h
      };

      if (!timeAtKphArray || timeAtKphArray.length === 0) return zones;

      const zoneKeys = ['stationary', 'jogging', 'lowIntensity', 'mediumIntensity', 'highIntensity', 'sprint'];

      timeAtKphArray.forEach((bucket, index) => {
        if (index < zoneKeys.length) {
          zones[zoneKeys[index]] = bucket.value || 0;
        }
      });

      return zones;
    },

    /**
     * Format distance in meters to km or m
     */
    formatPlayerDataDistance(meters) {
      if (!meters) return '0m';
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)}km`;
      }
      return `${Math.round(meters)}m`;
    },

    /**
     * Calculate percentage for half distance bar
     */
    getHalfPercentage(halfDistance) {
      if (!this.playerDataGpsMetrics) return 0;
      const total = this.playerDataGpsMetrics.firstHalfDistance + this.playerDataGpsMetrics.secondHalfDistance;
      if (total === 0) return 0;
      return (halfDistance / total) * 100;
    },

    async updateUserGamePerformanceReport() {
      try {
        const userData = {
          action: "updateUserGamePerformanceReport",
          ...this.editGamePerformanceData,
        };

        const response = await Games.find(userData).catch((error) => {
          console.log(error);
        });

        if (response.status === 200) {
          this.showEditPlayerperformanceModal = false;
          this.showAllGameReportsModal = false;
          this.loadPlayerMinsPerformanceDataForDate(
            this.editGamePerformanceData.gameId,
            this.editGamePerformanceData.matchDate,
          );
          this.$emit("loadPlayersProfilePerformanceData");
          this.$emit("getCoachesGamePerformanceReport");
          this.$emit("getThisSeasionsCoachesGamePerformanceReports");

          // Emit the new event to refresh parent data
          this.$emit("refreshSeasonsGamePerformanceReports");

          this.$toast.info("Game Performance Report updated", {
            title: "Saved",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "success",
          });
        }
      } catch (error) {
        console.error("Error updating game performance report", error);
      }
    },
    openeditPlayerperformanceModal(selectedGameReport) {
      this.editGamePerformanceData = selectedGameReport;
      this.showEditPlayerperformanceModal = true;
    },
    getReportClass(session) {
      const hasPlayerReport =
        this.checkPlayerPerformanceReportIsNotEmpty(session);
      const hasCoachReport = this.checkIfCoachReportExists(session.gameId);
      return hasPlayerReport && hasCoachReport
        ? "has-report"
        : "report-missing";
    },
    // Age-group label for a team's birth year, e.g. 2007 -> "U19".
    teamULabel(ageGroup) {
      const year = parseInt(ageGroup, 10);
      if (isNaN(year)) return "";
      return "U" + (new Date().getFullYear() - year);
    },
    // Flag a match as playing UP/DOWN: only for non-primary teams, comparing
    // the game's team age_group (birth year) to the player's.
    reportLevel(session) {
      const primaryTeamId = this.selectedUserData && this.selectedUserData.teamId;
      if (!session.teamId || Number(session.teamId) === Number(primaryTeamId)) {
        return null;
      }
      const playerYear = parseInt(
        this.selectedUserData && this.selectedUserData.age_group,
        10
      );
      const teamYear = parseInt(session.team_age_group, 10);
      if (isNaN(playerYear) || isNaN(teamYear)) return null;
      if (teamYear < playerYear) return "up";
      if (teamYear > playerYear) return "down";
      return null;
    },
    checkIfCoachReportExists(gameId) {
      return this.currentSeasonCoachReports.some(
        (report) => report.gameId === gameId,
      );
    },
    checkPlayerPerformanceReportIsNotEmpty(session) {
      const requiredKeys = [
        "technically_improvements",
        "technically_positives",
        "tactility_improvements",
        "tactility_positives",
        "physicality_improvements",
        "physicality_positives",
        "mentally_improvements",
        "mentally_positives",
      ];
      return requiredKeys.some(
        (key) => session[key] !== null && session[key] !== "",
      );
    },
    formatDateForHuman(date) {
      return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    getImgUrl(pic) {
      // Empty stays empty (callers rely on rendering nothing); a stored
      // "default-image.png" resolves to the neutral avatar via avatarUrl.
      return pic !== undefined && pic !== "" ? avatarUrl(pic) : "";
    },
    stripHtml(html) {
      return String(html === null || html === undefined ? "" : html).replace(
        /<[^>]*>/g,
        " ",
      );
    },
    // Open the most recent game automatically so the detail pane is never a
    // "nothing selected" dead end. Keeps the current selection if it still
    // exists in the active season.
    autoSelectLatestReport() {
      if (this.selectedSeason === "summary") return;
      if (this.sortedSeasonReports.length === 0) {
        this.selectedGameReport = {};
        return;
      }
      const currentId = this.selectedGameReport && this.selectedGameReport.gameId;
      const stillListed =
        currentId !== undefined &&
        this.sortedSeasonReports.some((r) => r.gameId === currentId);
      if (!stillListed) {
        this.viewFullReport(this.sortedSeasonReports[0].gameId, false);
      }
    },
    async viewFullReport(gameId, scrollToReport = true) {
      this.populateSelectedGameFullPerformanceReportsForUser(gameId);

      // On small screens the report renders below the table — bring it into
      // view so tapping a row visibly does something. Skipped for automatic
      // selection so opening the modal doesn't jump past the list.
      this.$nextTick(() => {
        if (
          scrollToReport &&
          window.innerWidth < 1024 &&
          this.$refs.reportDetail
        ) {
          this.$refs.reportDetail.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });

      // Load Eyeball data for this game
      const usersReport = this.currentSeasonReports.find((r) => r.gameId === gameId);
      if (usersReport) {
        this.destroyHLSPlayer();
        this.eyeBallSelectedGameData = {};
        this.eyeballPlayerData = null;
        await this.loadEyeballGameData(usersReport.matchDate);

        // If Eyeball data was found for this player, update the report with fallback values
        if (this.eyeballPlayerData) {
          const hasValue = (val) => val !== null && val !== undefined && val !== "" && val !== 0 && val !== "0" && !isNaN(val);
          if (!hasValue(this.selectedGameReport.goals)) {
            this.selectedGameReport = { ...this.selectedGameReport, goals: this.eyeballPlayerData.goals };
          }
          if (!hasValue(this.selectedGameReport.timeOnPitch)) {
            this.selectedGameReport = { ...this.selectedGameReport, timeOnPitch: this.eyeballPlayerData.minutes };
          }
          if (!this.selectedGameReport.postion_played || this.selectedGameReport.postion_played === "") {
            this.selectedGameReport = { ...this.selectedGameReport, postion_played: this.eyeballPlayerData.position };
          }
        }
      }
    },
    populateSelectedGameFullPerformanceReportsForUser(gameId) {
      // Use computed properties to get the correct season data
      const usersReport = this.currentSeasonReports.find(
        (report) => report.gameId === gameId,
      );
      const coachReport =
        this.currentSeasonCoachReports.find(
          (report) => report.gameId === gameId,
        ) || {};

      // Compute card display from coach report's yellow_card and red_card
      let cardDisplay = "None";
      const hasYellow = coachReport.yellow_card === true || coachReport.yellow_card === 1 || coachReport.yellow_card === "1";
      const hasRed = coachReport.red_card === true || coachReport.red_card === 1 || coachReport.red_card === "1";
      if (hasYellow && hasRed) {
        cardDisplay = "Yellow + Red";
      } else if (hasRed) {
        cardDisplay = "Red";
      } else if (hasYellow) {
        cardDisplay = "Yellow";
      }

      // Parse coach positions_played (comma-separated) into individual fields
      let coachPostionPlayed = "";
      let coachPostionPlayedTwo = "";
      if (coachReport.positions_played && typeof coachReport.positions_played === 'string') {
        const positions = coachReport.positions_played.split(',');
        coachPostionPlayed = positions[0] || "";
        coachPostionPlayedTwo = positions[1] || "";
      }

      this.selectedGameReport = {
        id: usersReport.id,
        // Use coach report stats as primary source, fall back to user report
        assists: parseInt(coachReport.assists) || parseInt(usersReport.assists) || 0,
        card: cardDisplay,
        clean_sheet: usersReport.clean_sheet,
        goals: parseInt(coachReport.goals) || parseInt(usersReport.goals) || 0,
        matchDate: usersReport.matchDate,
        // Use coach report session_mins as timeOnPitch, fall back to user report
        timeOnPitch: parseInt(coachReport.session_mins) || parseInt(usersReport.timeOnPitch) || 0,
        opposition: usersReport.opposition,
        player_self_rating: usersReport.player_self_rating,
        // Use coach report position if available, fall back to user report
        postion_played: coachPostionPlayed || usersReport.postion_played || "",
        postion_played_two: coachPostionPlayedTwo || usersReport.postion_played_two || "",
        technically_improvements: usersReport.technically_improvements,
        technically_positives: usersReport.technically_positives,
        tactility_improvements: usersReport.tactility_improvements,
        tactility_positives: usersReport.tactility_positives,
        physicality_improvements: usersReport.physicality_improvements,
        physicality_positives: usersReport.physicality_positives,
        mentally_improvements: usersReport.mentally_improvements,
        mentally_positives: usersReport.mentally_positives,
        pdps_improvements: usersReport.pdps_improvements,
        pdps_positives: usersReport.pdps_positives,
        overall_comments: usersReport.overall_comments,
        user_id: usersReport.user_id,
        coachReportId: coachReport.id,
        coach_name: coachReport.name || "",
        coachId: coachReport.coachId,
        game_comments: coachReport.game_comments || "",
        game_improvements: coachReport.game_improvements || "",
        player_rating: coachReport.player_rating || "Not Rated",
        userImage: coachReport.userImage || "",
        gameId: usersReport.gameId,
        created_date: usersReport.created_date,
        gameType: usersReport.gameType,
      };

      this.loadPlayerMinsPerformanceDataForDate(gameId, usersReport.matchDate);

      // Load PlayerData GPS data for PTFC club
      this.loadPlayerDataGpsForMatch(usersReport.matchDate, this.selectedUserData.name);

      this.playerReportNotEmpty = this.checkPlayerPerformanceReportIsNotEmpty(
        this.selectedGameReport,
      );
    },
    getGameInfromationFromId(gameId) {
      return this.currentSeasonReports.find(
        (report) => report.gameId === gameId,
      );
    },
    hideCoachPerformanceModel() {
      this.showNewEditcoachGamePerformanceModal = false;
    },
    downloadGameReportPDF() {
      const pdf = new JsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let yPosition = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (neededSpace = 20) => {
        if (yPosition + neededSpace > 280) {
          pdf.addPage();
          yPosition = margin;
        }
      };

      // Add header with blue background
      pdf.setFillColor(0, 85, 150);
      pdf.rect(0, 0, pageWidth, 45, "F");

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont(undefined, "bold");
      pdf.text("Game Assessment Report", margin, 20);

      pdf.setFontSize(12);
      pdf.setFont(undefined, "normal");
      pdf.text(this.selectedUserData.name || "Player", margin, 30);
      pdf.text(
        this.formatDateForHuman(this.selectedGameReport.matchDate),
        pageWidth - margin,
        20,
        { align: "right" }
      );
      pdf.text(
        `vs ${this.selectedGameReport.opposition || "Opposition"}`,
        pageWidth - margin,
        30,
        { align: "right" }
      );

      pdf.setTextColor(0, 0, 0);
      yPosition = 55;

      // Game Details Section
      pdf.setFontSize(16);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(0, 85, 150);
      pdf.text("Game Details", margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(11);
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(60, 60, 60);

      const gameDetails = [
        ["Match Date:", this.formatDateForHuman(this.selectedGameReport.matchDate)],
        ["Opposition:", this.selectedGameReport.opposition || "N/A"],
        ["Position Played:", this.selectedGameReport.postion_played || "N/A"],
        ["Second Position:", this.selectedGameReport.postion_played_two || "N/A"],
        ["Goals:", String(this.selectedGameReport.goals || 0)],
        ["Assists:", String(this.selectedGameReport.assists || 0)],
        ["Card:", this.selectedGameReport.card || "None"],
        ["Time on Pitch:", `${this.selectedGameReport.timeOnPitch || 0} mins`],
      ];

      gameDetails.forEach(([label, value]) => {
        if (value && value !== "N/A") {
          pdf.setFont(undefined, "bold");
          pdf.text(label, margin, yPosition);
          pdf.setFont(undefined, "normal");
          pdf.text(value, margin + 45, yPosition);
          yPosition += 7;
        }
      });

      yPosition += 10;

      // Player Self Assessment Section
      checkNewPage(40);
      pdf.setFontSize(16);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(0, 85, 150);
      pdf.text("Player Self Assessment", margin, yPosition);
      yPosition += 10;

      // Assessment areas
      this.assessmentAreas.forEach((area) => {
        checkNewPage(30);
        pdf.setFontSize(13);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(0, 60, 120);
        pdf.text(area.title, margin, yPosition);
        yPosition += 8;

        // Improvements
        pdf.setFontSize(11);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(80, 80, 80);
        pdf.text("Improvements:", margin, yPosition);
        yPosition += 6;

        pdf.setFont(undefined, "normal");
        pdf.setTextColor(60, 60, 60);
        const improvements = this.selectedGameReport[area.improvementsKey] || "No comments";
        const improvementLines = pdf.splitTextToSize(improvements.replace(/<[^>]*>/g, ""), contentWidth);
        checkNewPage(improvementLines.length * 5 + 10);
        pdf.text(improvementLines, margin, yPosition);
        yPosition += improvementLines.length * 5 + 5;

        // Positives
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(80, 80, 80);
        pdf.text("Positives:", margin, yPosition);
        yPosition += 6;

        pdf.setFont(undefined, "normal");
        pdf.setTextColor(60, 60, 60);
        const positives = this.selectedGameReport[area.positivesKey] || "No comments";
        const positiveLines = pdf.splitTextToSize(positives.replace(/<[^>]*>/g, ""), contentWidth);
        checkNewPage(positiveLines.length * 5 + 10);
        pdf.text(positiveLines, margin, yPosition);
        yPosition += positiveLines.length * 5 + 10;
      });

      // Coach Assessment Section
      if (this.selectedGameReport.coachReportId) {
        checkNewPage(50);
        pdf.setFontSize(16);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(0, 85, 150);
        pdf.text("Coach Assessment", margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(11);
        pdf.setFont(undefined, "normal");
        pdf.setTextColor(60, 60, 60);

        // Coach info
        pdf.setFont(undefined, "bold");
        pdf.text("Coach:", margin, yPosition);
        pdf.setFont(undefined, "normal");
        pdf.text(this.selectedGameReport.coach_name || "N/A", margin + 25, yPosition);
        yPosition += 7;

        // Rating
        pdf.setFont(undefined, "bold");
        pdf.text("Rating:", margin, yPosition);
        pdf.setFont(undefined, "normal");
        const rating = this.isStringNumberBetween0And5(this.selectedGameReport.player_rating)
          ? this.convertCoachRatingToWords(this.selectedGameReport.player_rating)
          : this.selectedGameReport.player_rating || "Not Rated";
        pdf.text(rating, margin + 25, yPosition);
        yPosition += 10;

        // Positive comments
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(80, 80, 80);
        pdf.text("Positive Comments:", margin, yPosition);
        yPosition += 6;

        pdf.setFont(undefined, "normal");
        pdf.setTextColor(60, 60, 60);
        const positiveComments = this.selectedGameReport.game_comments || "No comments";
        const positiveCommentLines = pdf.splitTextToSize(positiveComments.replace(/<[^>]*>/g, ""), contentWidth);
        checkNewPage(positiveCommentLines.length * 5 + 10);
        pdf.text(positiveCommentLines, margin, yPosition);
        yPosition += positiveCommentLines.length * 5 + 8;

        // Improvement comments
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(80, 80, 80);
        pdf.text("Areas for Improvement:", margin, yPosition);
        yPosition += 6;

        pdf.setFont(undefined, "normal");
        pdf.setTextColor(60, 60, 60);
        const improvementComments = this.selectedGameReport.game_improvements || "No comments";
        const improvementCommentLines = pdf.splitTextToSize(improvementComments.replace(/<[^>]*>/g, ""), contentWidth);
        checkNewPage(improvementCommentLines.length * 5 + 10);
        pdf.text(improvementCommentLines, margin, yPosition);
        yPosition += improvementCommentLines.length * 5 + 10;
      } else {
        checkNewPage(20);
        pdf.setFontSize(14);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(200, 0, 0);
        pdf.text("Coach Assessment: Not Completed", margin, yPosition);
        yPosition += 15;
      }

      // Footer
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${i} of ${pageCount}`,
          pageWidth / 2,
          290,
          { align: "center" }
        );
        pdf.text(
          `Generated on ${new Date().toLocaleDateString("en-GB")}`,
          pageWidth - margin,
          290,
          { align: "right" }
        );
      }

      // Generate filename and download
      const playerName = (this.selectedUserData.name || "Player").replace(/\s+/g, "_");
      const matchDate = this.selectedGameReport.matchDate
        ? new Date(this.selectedGameReport.matchDate).toISOString().split("T")[0]
        : "game";
      const filename = `Game_Report_${playerName}_${matchDate}.pdf`;

      pdf.save(filename);

      this.$toast.success("PDF downloaded successfully", {
        title: "Download Complete",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "success",
      });
    },
  },
  beforeUnmount() {
    this.destroyHLSPlayer();
  },
};
</script>

<style scoped lang="scss">
.season-selector {
  margin-bottom: 1rem;

  .btn-group {
    .btn {
      font-size: 0.9rem;
      padding: 0.5rem 1.5rem;

      &.btn-primary {
        background-color: #47a9f9;
        border-color: #47a9f9;
      }

      &.btn-outline-primary {
        color: #47a9f9;
        border-color: #47a9f9;

        &:hover {
          background-color: #47a9f9;
          border-color: #47a9f9;
        }
      }
    }
  }
}

.view-all-reports {
  border: 1px solid #c3cdd5;
  padding: 1rem;
  display: flex;
  margin-bottom: 1rem;
  border-radius: 4px;
  font-weight: bold;
  text-decoration: none;
  min-width: 18rem;

  &:hover {
    background: #47a9f9;
    color: white;
  }
}

.player-game-report {
  border: 1px solid #dee2e6;
  padding: 1rem;

  .report-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;

    h3 {
      margin-bottom: 0;
      font-size: 1.2rem;
    }

    .pdf-download-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      i {
        font-size: 1.1rem;
      }
    }
  }

  h3 {
    margin-bottom: 2rem;
    font-size: 1.2rem;
  }

  h4 {
    font-size: 1.2rem;
    text-transform: uppercase;
  }

  .player-assessment-report,
  .coach-assessment-report {
    border-top: 1px dashed #dee2e6;
    margin-top: 1rem;
    padding-top: 1rem;
    border-bottom: 1px dashed #c0c4c8;
  }
}

.full-player-report {
  .label {
    font-weight: bold;
    margin-bottom: 0.5rem;
  }

  .value {
    display: inline-block;
    width: 13rem;
    font-weight: 400;
  }
}

.reports-table-wrap {
  max-height: 26rem;
  overflow-y: auto;
  align-self: flex-start;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.game-assesment-reports-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
  }

  tbody tr {
    cursor: pointer;

    &:hover td {
      background: #f4f7f5;
    }

    &.selected td {
      background: #e8f2ec;
    }
  }
}

.game-assesment-reports-table th,
.game-assesment-reports-table td {
  text-align: left;
  vertical-align: middle;
}

.report-chips {
  white-space: nowrap;
}

.chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 4px;
}

.chip-done {
  background: #e6f4ea;
  color: #1d7a3a;
}

.chip-missing {
  background: #f0f1f3;
  color: #6b7280;
}

.action-col {
  text-align: right;
  white-space: nowrap;
}

.report-headline {
  h3 {
    margin: 0;
    font-size: 1.25rem;
  }

  .report-subline {
    margin: 0.15rem 0 0;
    color: #5b6770;
    font-size: 0.9rem;
  }
}

.assessments-compare {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media screen and (min-width: 1024px) {
  .assessments-compare {
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
}

.collapse-section {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin: 1rem 0;

  >summary {
    cursor: pointer;
    padding: 0.75rem 1rem;
    font-weight: 600;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    &::after {
      content: "▸";
      float: right;
      color: #6b7280;
      transition: transform 0.15s ease;
    }
  }

  &[open]>summary::after {
    transform: rotate(90deg);
  }

  >*:not(summary) {
    padding: 0 1rem 1rem;
  }
}

.empty-state {
  text-align: center;
  margin-top: 20px;
}

.empty-state img {
  max-width: 200px;
  margin-bottom: 10px;
}

.small-image-name-wrapper {
  margin-bottom: 1rem;
}

.player-coach-performance-image {
  border-bottom: 1px dotted #dddddd;
  padding: 1rem 0;
  display: flex;

  img {
    width: 4rem;
    height: 4rem;
    border-radius: 100px;
    margin-right: 1rem;
  }

  .user-name {
    font-weight: bold;
    font-size: 1.2rem;
  }
}

.small-image {
  border: 1px solid #a9b3bc;
  width: 4rem;
  border-radius: 100px;
  margin-right: 1rem;
}

.areas-wrapper {
  border: 1px solid #dee2e6;
  padding: 1rem;
  margin-bottom: 1rem;
}

.idp-game-targets {
  border-top: 1px dashed #dee2e6;
  margin-top: 2rem;
  padding-top: 2rem;

  .label {
    .value {
      width: 10rem;
      display: inline-block;
      font-weight: normal;
      font-size: 1.2rem;
    }
  }
}

.target-outline {
  margin-bottom: 0.5rem;
  border-bottom: 1px dashed #c0c4c8;
  padding-bottom: 1rem;
  margin-bottom: 1rem;

  img {
    width: 2rem;
    padding-right: 0.5rem;
  }

  span:first-of-type {
    display: inline-block;
    min-width: 22rem;
    margin-bottom: 1rem;
    margin-right: 1rem;
  }

  span.complete {
    border: 1px solid #08a10f;
    padding: 0.3rem;
    border-radius: 4px;
    color: #08a10f;
  }

  span.not-complete {
    border: 1px solid #d40035;
    padding: 0.3rem;
    border-radius: 4px;
    color: #d40035;
  }
}

.small-date {
  display: block;
  font-size: 1rem;
  color: #aeb8c0;
  text-transform: none;
  font-weight: 100;
}

.not-complete {
  border: 1px solid #d40035;
}

.has-report {
  border-left: 2px solid #08a10f;
}

.report-level {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  background: #eef1f4;
  color: #4b5563;
}

.report-missing {
  border-left: 2px solid #d1a03c;
}

.missing-coach-assessment-report {
  border: 1px solid #d40035;
  padding: 1rem;
  margin: 1rem 0;

  .small-image[data-v-0b45501a] {
    opacity: 0.3;
  }
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #47a9f9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media screen and (min-width: 1024px) {
  .all-game-reports {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    .player-game-report {
      flex: 1;
    }
  }

  .player-game-report {
    padding: 2rem;

    .player-assessment-report,
    .coach-assessment-report {
      margin: 2rem 0;
      padding: 2rem 0;
    }
  }
}

.coach-stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  .form__item {
    flex: 1;
    min-width: 120px;
    margin-bottom: 0;

    .form__label {
      font-size: 0.85rem;
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.5rem;
    }
  }

  .card-checkboxes {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding-top: 0.5rem;

    .yellow-card-checkbox,
    .red-card-checkbox {
      display: flex;
      align-items: center;
      gap: 0.25rem;

      .card-icon {
        font-size: 1.2rem;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .coach-stats-row {
    flex-direction: column;

    .form__item {
      width: 100%;
    }
  }
}

/* Summary Dashboard Styles */
.summary-dashboard {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 2rem;

  h3 {
    margin: 0 0 1.5rem 0;
    color: #333;
    font-size: 1.5rem;
    text-align: center;
  }
}

.timeline-picker {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .form__label {
    font-weight: 600;
    color: #333;
    margin: 0;
    white-space: nowrap;
  }

  .b-form-select,
  select {
    max-width: 200px;
  }
}

.stats-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }

    .stat-icon {
      width: 50px;
      height: 50px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;

      &.games {
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
      }

      &.goals {
        background: linear-gradient(135deg, #11998e, #38ef7d);
        color: white;
      }

      &.assists {
        background: linear-gradient(135deg, #f093fb, #f5576c);
        color: white;
      }

      &.time {
        background: linear-gradient(135deg, #4facfe, #00f2fe);
        color: white;
      }
    }

    .stat-content {
      display: flex;
      flex-direction: column;

      .stat-number {
        font-size: 1.75rem;
        font-weight: 700;
        color: #333;
        line-height: 1;
      }

      .stat-label {
        font-size: 0.85rem;
        color: #666;
        margin-top: 0.25rem;
      }
    }
  }
}

.stats-secondary {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;

  .stat-card-small {
    background: white;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    flex: 1;
    min-width: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .stat-label {
      font-size: 0.8rem;
      color: #666;
      margin-bottom: 0.5rem;
    }

    .stat-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
    }

    &.yellow {
      border-left: 4px solid #ffc107;

      .stat-value {
        color: #856404;
      }
    }

    &.red {
      border-left: 4px solid #dc3545;

      .stat-value {
        color: #721c24;
      }
    }
  }
}

.ratings-summary {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h4 {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    color: #333;
  }

  .ratings-breakdown {
    margin-bottom: 1rem;
  }

  .rating-bar-container {
    margin-bottom: 1rem;
  }

  .rating-bar {
    display: flex;
    height: 24px;
    border-radius: 12px;
    overflow: hidden;
    background: #e9ecef;

    .rating-fill {
      height: 100%;
      transition: width 0.3s ease;

      &.excellent {
        background: #28a745;
      }

      &.good {
        background: #47a9f9;
      }

      &.average {
        background: #ffc107;
      }

      &.belowAverage {
        background: #fd7e14;
      }

      &.poor {
        background: #dc3545;
      }
    }
  }

  .rating-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.85rem;
      color: #666;

      .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;

        &.excellent {
          background: #28a745;
        }

        &.good {
          background: #47a9f9;
        }

        &.average {
          background: #ffc107;
        }

        &.belowAverage {
          background: #fd7e14;
        }

        &.poor {
          background: #dc3545;
        }
      }
    }
  }

  .coach-reports-count {
    text-align: center;
    font-size: 0.85rem;
    color: #888;
    margin: 1rem 0 0 0;
  }
}

.positions-summary {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h4 {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    color: #333;
  }

  .positions-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;

    .position-item {
      background: #f0f7ff;
      border: 1px solid #cce5ff;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .position-name {
        font-weight: 600;
        color: #333;
      }

      .position-count {
        background: #47a9f9;
        color: white;
        padding: 0.25rem 0.5rem;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: 600;
      }
    }
  }
}

.completion-summary {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h4 {
    margin: 0 0 1.5rem 0;
    font-size: 1.1rem;
    color: #333;
    text-align: center;
  }

  .completion-stats {
    display: flex;
    justify-content: center;
    gap: 3rem;

    .completion-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;

      .completion-circle {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: #e9ecef;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 1.1rem;
        color: #666;
        border: 3px solid #dee2e6;

        &.complete {
          background: #d4edda;
          color: #155724;
          border-color: #28a745;
        }
      }

      .completion-label {
        font-size: 0.9rem;
        color: #666;
        font-weight: 500;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .summary-dashboard {
    padding: 1rem;
  }

  .timeline-picker {
    flex-direction: column;
    align-items: stretch;

    .b-form-select,
    select {
      max-width: none;
      width: 100%;
    }
  }

  .stats-overview {
    grid-template-columns: repeat(2, 1fr);

    .stat-card {
      flex-direction: column;
      text-align: center;

      .stat-icon {
        margin-bottom: 0.5rem;
      }
    }
  }

  .stats-secondary {
    .stat-card-small {
      min-width: calc(50% - 0.5rem);
    }
  }

  .rating-legend {
    gap: 0.5rem;
  }

  .completion-stats {
    gap: 1.5rem;

    .completion-circle {
      width: 70px;
      height: 70px;
      font-size: 1rem;
    }
  }
}

/* PlayerData GPS Section Styles */
.playerdata-gps-section {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    color: #64748b;
  }

  h4 {
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #1e293b;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .playerdata-badge {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .session-meta-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    .session-type {
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;

      &.match {
        background-color: #dbeafe;
        color: #1d4ed8;
      }

      &.training {
        background-color: #dcfce7;
        color: #16a34a;
      }
    }

    .match-info {
      color: #475569;
      font-size: 0.9rem;

      .score {
        font-weight: 600;
        margin-left: 0.5rem;
        color: #1e293b;
      }
    }
  }

  .playerdata-metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .metric-card {
    background: white;
    border-radius: 10px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .metric-icon {
      font-size: 1.5rem;
    }

    .metric-content {
      flex: 1;
    }

    .metric-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1e293b;
    }

    .metric-label {
      font-size: 0.75rem;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    &.distance .metric-value {
      color: #2563eb;
    }

    &.speed .metric-value {
      color: #dc2626;
    }

    &.sprint .metric-value {
      color: #f59e0b;
    }

    &.hsr .metric-value {
      color: #059669;
    }

    &.acceleration .metric-value {
      color: #7c3aed;
    }

    &.deceleration .metric-value {
      color: #db2777;
    }

    &.sprint-distance .metric-value {
      color: #ea580c;
    }

    &.pace .metric-value {
      color: #0891b2;
    }
  }

  .half-breakdown {
    background: white;
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1rem;

    h5 {
      margin: 0 0 0.75rem 0;
      font-size: 0.9rem;
      color: #475569;
    }

    .half-bars {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .half-bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .half-label {
        width: 60px;
        font-size: 0.8rem;
        color: #64748b;
      }

      .bar-fill {
        height: 24px;
        border-radius: 4px;
        transition: width 0.3s ease;
      }

      .half-value {
        font-size: 0.85rem;
        font-weight: 600;
        color: #1e293b;
        min-width: 60px;
      }

      &.first-half .bar-fill {
        background: linear-gradient(90deg, #3b82f6, #60a5fa);
      }

      &.second-half .bar-fill {
        background: linear-gradient(90deg, #10b981, #34d399);
      }
    }
  }

  .speed-zones-section {
    background: white;
    border-radius: 10px;
    padding: 1rem;

    h5 {
      margin: 0 0 0.75rem 0;
      font-size: 0.9rem;
      color: #475569;
    }

    .speed-zones-bar {
      display: flex;
      height: 30px;
      border-radius: 6px;
      overflow: hidden;
      margin-bottom: 0.75rem;

      .zone {
        min-width: 2px;
        transition: width 0.3s ease;

        &.stationary {
          background-color: #94a3b8;
        }

        &.jogging {
          background-color: #22c55e;
        }

        &.low-intensity {
          background-color: #84cc16;
        }

        &.medium-intensity {
          background-color: #eab308;
        }

        &.high-intensity {
          background-color: #f97316;
        }

        &.sprint {
          background-color: #ef4444;
        }
      }
    }

    .speed-zones-legend {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      font-size: 0.75rem;
      color: #64748b;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;

        &.stationary {
          background-color: #94a3b8;
        }

        &.jogging {
          background-color: #22c55e;
        }

        &.low-intensity {
          background-color: #84cc16;
        }

        &.medium-intensity {
          background-color: #eab308;
        }

        &.high-intensity {
          background-color: #f97316;
        }

        &.sprint {
          background-color: #ef4444;
        }
      }
    }
  }

  .no-playerdata-gps {
    padding: 1rem;
    text-align: center;

    .info-text {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      color: #64748b;
      font-size: 0.9rem;
    }
  }
}

/* Responsive adjustments for PlayerData GPS */
@media (max-width: 768px) {
  .playerdata-gps-section {
    padding: 1rem;

    .playerdata-metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .metric-card {
      flex-direction: column;
      text-align: center;
      padding: 0.75rem;

      .metric-icon {
        font-size: 1.25rem;
      }

      .metric-value {
        font-size: 1rem;
      }
    }

    .speed-zones-legend {
      justify-content: center;
    }
  }

  /* Season Selector Responsive */
  .season-selector {
    .btn-group {
      flex-direction: column;

      .btn {
        width: 100%;
        border-radius: 0.375rem !important;
        margin-bottom: 0.5rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .season-selector {
    margin-bottom: 0.75rem;

    .btn-group {
      .btn {
        font-size: 0.85rem;
        padding: 0.5rem 0.75rem;
      }
    }
  }
}

.eyeball-match-section {
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;

  h4 {
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .eyeball-video-wrapper {
    margin-bottom: 1rem;
  }

  .eyeball-team-lines {
    .table-sm {
      font-size: 0.85rem;
      th, td { padding: 0.3rem 0.5rem; }
    }
    h5 {
      font-size: 0.9rem;
      font-weight: 600;
      margin: 0.75rem 0 0.25rem;
    }
  }
}

/* --- Layout polish (kept last so these win the cascade) ------------------- */

/* Cap content width on large monitors so text stays readable. */
.season-selector,
.summary-dashboard,
.all-game-reports,
.modal-fullscreen h3 {
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
}

.season-selector {
  display: flex;
  justify-content: flex-start;
}

/* Player vs coach assessments as matched cards inside the compare grid. */
.full-player-report .assessments-compare .player-assessment-report,
.full-player-report .assessments-compare .coach-assessment-report,
.full-player-report .assessments-compare .missing-coach-assessment-report {
  border: 1px solid #e2e7e4;
  border-radius: 6px;
  margin: 0;
  padding: 1.25rem 1.5rem;
  background: #fbfcfb;
}

/* Flatten the inner assessment-areas box — the card already provides the
   border, so the nested one read as a box-in-a-box. */
.full-player-report .assessments-compare .areas-wrapper {
  border: none;
  padding: 1rem 0 0;
  margin-bottom: 0;
}

/* Coach comments: label on its own line, comment below, instead of a ragged
   two-column wrap. */
.full-player-report .assessments-compare .coach-assessment-report p .value {
  display: block;
  margin-bottom: 2px;
}

/* When a season has no reports, let the empty state own the row. */
.all-game-reports .list-empty-state {
  flex: 1 1 100%;
  margin: 2.5rem 0;
}

/* --- "Edit your game performance" form ----------------------------------- */
.performance-form {
  .pf-card {
    border: 1px solid #e2e7e4;
    border-radius: 6px;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
    background: #fbfcfb;
  }

  .pf-card__title {
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin: 0 0 0.25rem;
  }

  .pf-card__hint {
    color: #6b7a85;
    font-size: 0.85rem;
    margin: 0 0 0.75rem;
  }

  .pf-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.25rem 1.25rem;

    .form__item {
      margin-bottom: 0.5rem;
    }
  }

  .pf-field-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.35rem;

    .form__label {
      margin-bottom: 0;
    }
  }

  .pf-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .pf-readonly-note {
    color: #8a6d1a;
    font-size: 0.9rem;
    margin: 0;
  }
}

@media screen and (min-width: 768px) {
  .performance-form {
    .pf-grid {
      grid-template-columns: repeat(3, 1fr);
    }

    .pf-grid--two {
      grid-template-columns: 1fr 1fr;
    }
  }
}
</style>
